/**
 * deal-detail/render-quote.ts — 견적 렌더 (섹션 / 생성·수정 모달 / 보기 모달)
 *
 * DealDetailView.ts 에서 분리 (정리 Step 2, 2026-07-03 — 분해 계획 v2 §2).
 * 이동만 — 동작·마크업 변화 없음. 전부 "state → HTML 문자열" 순수 함수.
 *
 * 이벤트는 data-action 문자열로 배선되므로 (Sola events 맵), 핸들러는
 * DealDetailView 의 메서드 그대로 — 렌더 위치와 무관하게 동작.
 *
 * 계산 규칙(마진/합계)은 lib/quote-math.ts 가 정본 (테스트로 동결).
 */

import * as api from '../../api/client';
import { escapeHtml, fmtDate } from '../../lib/cells';
import { withServerSuffix } from '../../lib/server-desc';
import { sourceSelectHtml, selectedSourceValue, type SourcePool } from '../../lib/source-options';
import {
  parseNum, parseInt0, rowCostKrw, serverRowCostKrw, computeRowMargin, summarizeRows, anyCostEntered,
  serverEffectiveSellKrw,
  computeQuoteTotals, quoteBlockReasons, type QuoteTotals, type RowMargin,
} from '../../lib/quote-math';
import {
  fmtThousands, needsShorthandFix, shorthandFixCompactHtml,
  type QuoteModalState, type QuoteModalRow, type QuoteViewModalState,
  type QuoteDownloadModalState,
} from './types';

// 약식 미적용 경고 — 행 기준(서버 헤더행은 자식 집계). row 는 camel/snake 혼용 허용.
function rowShorthandWarn(
  row: { lineType?: string; line_type?: string; deal_item_id?: string | null;
         isUnregistered?: boolean | null; isPending?: boolean | null;
         is_unregistered?: boolean | null; is_pending?: boolean | null },
  _all?: ReadonlyArray<unknown>,
): string {
  // 서버 헤더행엔 배지를 달지 않는다 — 실제 문제 품목(자식/개별 행)에만 뜬다.
  if ((row.lineType ?? row.line_type) === 'server') return '';
  return needsShorthandFix(row) ? shorthandFixCompactHtml() : '';
}
import {
  computeRentalLine, computeRentalTotals, rentalBlockReasons, defaultPresetIdFor,
} from '../../lib/quote-rental';

// ─── 포맷 헬퍼 ──────────────────────────────────────────────────────
function fmtKrw(n: number | null): string {
  if (n == null) return '-';
  return `₩ ${Math.round(n).toLocaleString('ko-KR')}`;
}

function fmtPct(n: number | null): string {
  if (n == null) return '-';
  return `${n.toFixed(1)}%`;
}

// 현재 미사용 — 과거 USD 표시/말줄임에 쓰던 것. 재사용 대비 export 로 보존.
export function fmtUsd(n: number | null): string {
  if (n == null) return '-';
  return `$ ${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export function truncate(s: string | null | undefined, n: number): string {
  if (!s) return '-';
  return s.length > n ? s.slice(0, n) + '…' : s;
}

// 소스 드롭다운 라벨 — 기획 포맷: "RFQ - YYYY/MM/DD 매입처 컨디션 가격"
//   예: "RFQ - 2026/06/05 BEIYI REF $194"
function quoteSourceLabel(s: api.DealRfqSource): string {
  const d = new Date(s.rfq_date);
  const date = Number.isNaN(d.getTime())
    ? ''
    : `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
  const cur   = s.price_currency === 'USD' ? '$' : '₩';
  const price = s.price_amount != null ? `${cur}${s.price_amount}` : '';
  const alt   = s.is_alternative ? '대체' : '';
  const cond  = s.sourced_condition || s.requested_condition || '미확인';   // sourced 없으면 니즈 요청 컨디션
  const parts = [date, s.supplier_name, alt, cond, price].filter(Boolean);
  return `RFQ - ${parts.join(' ')}`;
}

function fmtYmd(v: string): string {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? ''
    : `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * 레퍼런스 셀 — `판매단가` 바로 왼쪽의 전용 컬럼 (§4.10.42~44, QUOTE-009).
 *
 * 예전엔 품목 칸 안에 `<details>` 로 RFQ 이력까지 깔았다. 두 가지가 틀렸다:
 *   1) 자리 — 품목 칸이 세로로 길어져 표가 읽히지 않았다.
 *   2) 내용 — RFQ 와 재고는 **매입 소스를 고르는 근거**이므로 소스 dropdown 에만
 *      두고 레퍼런스에 중복 표시하지 않는다(§4.10.44).
 * 레퍼런스는 "과거에 이 파트를 얼마에 팔았나"만 답한다.
 *
 * 지금은 `최근 견적가`(발송된 견적)만 채운다. `최근 판매가`(채택/수주 실적)는
 * 아직 조회 경로가 없어 뒤로 미뤘다 — 데이터가 생기면 줄만 추가하면 된다.
 */
/** 레퍼런스 한 줄: `날짜 · 거래처(딜 링크) · ₩가격 · 견적/수주`. 인라인·팝오버 공용. */
function refLineHtml(p: api.DealQuotePrice): string {
  const price   = p.unit_price_krw != null ? `₩${Number(p.unit_price_krw).toLocaleString()}` : '-';
  const company = escapeHtml(p.customer_name || '-');
  // 거래처명 클릭 → 해당 딜 새 탭. router 가로채기를 피하려고 anchor 대신
  //   버튼 + window.open(DealListView 와 같은 방식).
  const dealCell = p.deal_id
    ? `<button type="button" class="deal-quote-ref-deal" data-action="quote-ref-open-deal" data-deal-id="${escapeHtml(p.deal_id)}" title="딜 새 탭으로 열기">${company}</button>`
    : company;
  const isWon = p.event_type === '수주';
  const evt   = isWon ? '수주' : '견적';
  return `<span class="deal-quote-ref-line">`
       + `${escapeHtml(fmtYmd(p.quoted_at))}<span class="deal-quote-ref-sep">·</span>`
       + `${dealCell}<span class="deal-quote-ref-sep">·</span>`
       + `${escapeHtml(price)}<span class="deal-quote-ref-sep">·</span>`
       + `<span class="deal-quote-ref-evt${isWon ? ' is-won' : ''}">${evt}</span>`
       + `</span>`;
}

/**
 * 레퍼런스 셀 — 타이틀 없음. 최신순 3줄 + (초과 시) "더보기" → 팝오버(최대 20).
 *
 * 정렬은 백엔드가 끝냈다(동일 거래처 최상단 → 날짜 최신). 여기서는 그대로 그린다.
 * 팝오버 열림 상태는 costPop 과 같은 규약 — 행 index 로 하나만 연다(m.refPopRow).
 * 오버레이와 패널은 **형제**다(패널 안 버튼이 오버레이 close 에 안 먹히게).
 */
function renderQuoteReferenceCell(
  priceMatches: api.DealQuotePrice[],
  idx:          number,
  openRow:      number | null,
): string {
  if (priceMatches.length === 0) {
    return '<span class="deal-quote-ref-empty">없음</span>';
  }
  const top  = priceMatches.slice(0, 3).map(refLineHtml).join('');
  const rest = priceMatches.length > 3;
  const more = rest
    ? `<button type="button" class="deal-quote-ref-more" data-action="quote-ref-more-open" data-row-index="${idx}">더보기 (${priceMatches.length})</button>`
    : '';
  const pop = (rest && openRow === idx)
    ? `<div class="deal-tag-pop-overlay" data-action="quote-ref-more-close"></div>`
      + `<div class="deal-quote-refpop" role="dialog" aria-label="레퍼런스 전체">`
      + `<div class="deal-quote-refpop-head">레퍼런스 · 최신순 ${Math.min(priceMatches.length, 20)}건</div>`
      + `<div class="deal-quote-refpop-body">${priceMatches.slice(0, 20).map(refLineHtml).join('')}</div>`
      + `</div>`
    : '';
  return `<div class="deal-quote-ref-group">${top}${more}${pop}</div>`;
}

/**
 * 서버 헤더행의 품목 칸 — **모델명 한 줄** + 보조줄(신품/중고 서버).
 *
 * 약식 요약 전체를 여기 쓰지 않는다(QUOTE-009). 구성 상세는 chevron 으로 펼쳐서
 * 구성품행으로 본다 — 요약을 통째로 박아 놓으면 행 하나가 화면 절반을 먹고,
 * 그 안의 값을 편집할 수도 없다.
 */
function serverHeaderPartCell(row: QuoteModalRow, expanded: ReadonlySet<string>): string {
  const key  = String(row.deal_item_id ?? '');
  const open = expanded.has(key);
  return `
    <button class="deal-quote-server-toggle" type="button"
            data-action="quote-server-toggle" data-server-key="${escapeHtml(key)}"
            aria-expanded="${open}"
            aria-label="서버 구성 ${open ? '접기' : '펼치기'}">
      <span class="deal-quote-server-chev">${open ? '▾' : '▸'}</span>
      <!-- 서버 디스크립션이 출력되는 자리. 딜 상세와 같은 규칙으로 끝에
           Server 를 덧붙인다 — 표시용이며 저장값은 건드리지 않는다
           (2026-08-20 서안드레: "서버 디스크립션이 출력되는 곳은 딜, 견적").
           row.description 은 이미 약식 요약의 첫 줄(샤시 줄)이다 —
           deal-detail/types.ts 가 split 으로 첫 줄만 담는다.
           주석에 백틱을 쓰지 말 것: 이 블록 전체가 템플릿 리터럴 안이다. -->
      <span class="deal-quote-server-model">${escapeHtml(withServerSuffix(row.description) || '서버')}</span>
      <span class="deal-quote-server-cond">${escapeHtml(row.condition ?? '서버')}</span>
    </button>`;
}

/**
 * 서버 그룹 외곽선 클래스 — 헤더부터 마지막 구성품까지를 한 덩어리로 두른다.
 *
 * 선이 없으면 펼친 구성품과 그 뒤에 오는 **개별 품목**이 구분되지 않는다.
 * 딜 상세 품목표와 같은 방식·같은 색이다(같은 물건이 두 화면에서 다르게
 * 보이면 안 된다).
 *
 * 아랫변을 누가 그리는지는 CSS 가 아니라 여기서 정한다:
 *   접힘(또는 자식 없음) → 헤더행 자신
 *   펼침 + 자식 있음     → 마지막 구성품행
 * `:not(.is-expanded)` 같은 CSS 판정만 쓰면 "펼쳤는데 자식이 없는" 서버에서
 * 프레임이 열린 채 남는다.
 *
 * rows 는 `헤더 → 그 자식들 → 다음` 순서로 정렬돼 있다고 가정한다
 * (orderQuoteRows / quote_item.sort_order 가 그 순서를 보장한다).
 */
export function serverFrameClass(
  rows:    readonly { lineType?: string | null; parentDealItemId?: string | null; deal_item_id?: string | null }[],
  idx:     number,
  visible: (row: { parentDealItemId?: string | null }) => boolean,
): string {
  const row = rows[idx]!;
  if (row.lineType === 'server') {
    const kids = rows.filter((r) => r.parentDealItemId != null
      && row.deal_item_id != null
      && String(r.parentDealItemId) === String(row.deal_item_id)
      && visible(r));
    return `deal-quote-server-row${kids.length === 0 ? ' is-frame-end' : ''}`;
  }
  if (row.parentDealItemId == null) return '';
  // 같은 부모의 마지막 보이는 자식인가.
  const same = rows.filter((r) => r.parentDealItemId != null
    && String(r.parentDealItemId) === String(row.parentDealItemId) && visible(r));
  const isLast = same.length > 0 && same[same.length - 1] === row;
  return `deal-quote-server-child${isLast ? ' is-frame-end' : ''}`;
}

/**
 * 서버 구성품행의 품목 칸 — `카테고리 파트넘버` + 둘째 줄 디스크립션.
 *
 * 디스크립션은 **카탈로그 원문**(2026-08-04, 서안드레). 약식은 서버를 한 줄로
 * 접어 보여줄 때 쓰는 표기라, 행마다 펼쳐 놓으면 오히려 정보가 줄어든다 —
 * '1Gb 4Port' 보다 'Broadcom BCM5719 Ethernet 1Gb 4-port BASE-T OCP3 Adapter'
 * 가 견적을 검토하는 데 쓸모 있다. 원문이 없으면(미등록 파트) 약식으로 폴백.
 */
/**
 * 기본 구성 품목 뱃지 — 마이그 174.
 * 소스·매입단가가 왜 잠겨 있는지를 이 한 조각이 설명한다.
 *
 * ★ 불변식: **뱃지가 붙는 행은 소스가 반드시 비활성**이다(그 역도 같다).
 *   그래서 뱃지는 행 종류(서버 자식/평면)로 가리지 않고 isBasePart 하나만 본다 —
 *   잠금 판정이 isBasePart 하나만 보기 때문이다. 한쪽만 분기하면 "왜 못 고르지"
 *   가 설명되지 않는 행이 생긴다.
 */
function basePartChip(on: boolean): string {
  return on ? '<span class="deal-quote-base-chip">기본</span>' : '';
}
// 미등록/임시 뱃지 — 딜 품목표(regBadge)와 동일 판정. 파트넘버 옆 인라인(기본 칩과 같은 자리).
function regChip(unreg?: boolean | null, pending?: boolean | null): string {
  if (pending) return ' <span class="cond-chip cond-임시등록">임시</span>';
  if (unreg)   return ' <span class="cond-chip cond-미등록">미등록</span>';
  return '';
}

function serverChildPartCell(row: QuoteModalRow): string {
  return `
    <div class="deal-quote-child-head">
      <span class="deal-quote-child-cat">${escapeHtml(row.specCategory ?? '-')}</span>
      <span class="deal-quote-child-part">${escapeHtml(row.option_part ?? '파트넘버 미지정')}</span>
      ${basePartChip(row.isBasePart)}${regChip(row.isUnregistered, row.isPending)}
    </div>
    <div class="deal-quote-child-desc">${escapeHtml(row.descriptionRaw ?? row.description ?? '')}</div>`;
}

// ─── 견적 섹션 (P9-UI Step 3a) ──────────────────────────────────────
// 상단: 총액 카드 3 (deal.final_*_krw — 채택된 quote 의 cache).
// 하단: quote 목록 표 (이 deal 의 모든 quote, 최근 위).
export function renderQuoteSection(
  full:        api.DealFull,
  quotes:      api.Quote[],
  quoteMenuId: string | null,
): string {
  return `
      <section class="deal-section">
        <h2 class="deal-section-title">견적</h2>

        ${renderQuoteCards(full)}

        ${renderQuoteListTable(quotes, full.accepted_quote_id, quoteMenuId)}
      </section>
    `;
}

// 총액 카드 3개 — 채택된 quote 의 deal cache (final_*_krw).
function renderQuoteCards(full: api.DealFull): string {
  const fmt = (n: number | null) => n == null ? '-' : `₩ ${n.toLocaleString('ko-KR')}`;
  const fmtRate = (n: number | null) => n == null ? '-' : `${n.toFixed(1)}%`;
  return `
      <div class="deal-quote-cards">
        <div class="deal-quote-card">
          <dt>견적 금액</dt>
          <dd>${escapeHtml(fmt(full.final_amount_krw))}</dd>
        </div>
        <div class="deal-quote-card">
          <dt>부가세 포함</dt>
          <dd>${escapeHtml(fmt(full.final_amount_with_vat_krw))}</dd>
        </div>
        <div class="deal-quote-card">
          <dt>마진</dt>
          <dd>
            ${escapeHtml(fmt(full.final_margin_krw))}
            <small class="text-muted">(${escapeHtml(fmtRate(full.final_margin_rate))})</small>
          </dd>
        </div>
      </div>
    `;
}

// quote 목록 표 — accepted 인 row 강조.
/**
 * 견적 유형 라벨 — 목록에서 판매/임대를 한눈에 가른다.
 *
 * 임대만 색을 준다. 판매가 기본값이고 대부분이라, 둘 다 칠하면 목록이
 * 시끄러워지고 오히려 임대가 눈에 안 띈다.
 *
 * quote_type 은 스키마상 필수(z.enum(QUOTE_TYPES))지만, 마이그 178 이전에
 * 만들어진 행은 DB 기본값 'SALE' 로 채워져 있다 — 즉 값이 없을 일은 없다.
 * 그래도 'RENTAL' 이 아니면 판매로 읽어, 어휘가 늘어도 목록이 빈칸이 되지 않게 한다.
 */
function renderQuoteTypeLabel(t: api.Quote['quote_type']): string {
  return t === 'RENTAL'
    ? '<span class="deal-quote-type deal-quote-type-rental">임대</span>'
    : '<span class="deal-quote-type">판매</span>';
}

function renderQuoteListTable(
  quotes:          api.Quote[],
  acceptedQuoteId: string | null,
  quoteMenuId:     string | null,
): string {
  if (quotes.length === 0) {
    return `
        <p class="text-muted text-sm" style="margin: 8px 0 16px;">
          아직 작성된 견적이 없습니다.
        </p>
      `;
  }
  const fmtKrwLocal = (n: number | null) =>
    n == null ? '-' : '₩ ' + n.toLocaleString('ko-KR');
  const rows = quotes.map((q) => {
    const isAccepted = acceptedQuoteId === q.id;
    const rowClass   = `deal-quote-row-click${isAccepted ? ' deal-quote-row-accepted' : ''}`;
    const marginCell = q.margin_rate == null ? '-' : `${q.margin_rate.toFixed(1)}%`;
    const menuOpen   = quoteMenuId === q.id;
    // 행 클릭 → 보기 모달. 작업(⋯)은 view-quote 보다 먼저 등록돼 중첩 우선.
    return `
        <tr class="${rowClass}" style="cursor:pointer" data-action="view-quote" data-quote-id="${escapeHtml(q.id)}">
          <td class="text-muted text-sm">${escapeHtml(fmtDate(q.created_at))}</td>
          <td class="text-muted text-sm"><code>${escapeHtml(q.quote_no)}</code></td>
          <td>${escapeHtml(q.name)}${q.is_outdated ? ` <span class="quote-outdated-flag" title="${escapeHtml(q.name)} · 작성 후 변경: ${escapeHtml(q.outdated_parts ?? '품목 변경됨')}" aria-label="구성 수정됨">❗</span>` : ''}</td>
          <td>${renderQuoteStatusChip(q.status)}</td>
          <td class="text-center deal-quote-type-cell">${renderQuoteTypeLabel(q.quote_type)}</td>
          <td class="text-right">${fmtKrwLocal(q.total_amount_krw)}</td>
          <td class="text-right">${fmtKrwLocal(q.amount_with_vat_krw)}</td>
          <td class="text-right">${marginCell}</td>
          <td class="text-center deal-tag-cell">
            <button class="btn btn-ghost btn-icon deal-quote-menu-trigger" type="button"
                    data-action="quote-menu-open" data-quote-id="${escapeHtml(q.id)}"
                    title="견적 작업" aria-label="견적 작업">⋯</button>
            ${menuOpen ? `
              <div class="deal-tag-pop-overlay" data-action="quote-menu-close"></div>
              <div class="deal-tag-pop deal-quote-menu">
                <button class="deal-tag-opt" type="button"
                        style="width:100%;border:0;background:transparent;text-align:left;font:inherit"
                        data-action="quote-edit-paper" data-quote-id="${escapeHtml(q.id)}">지면 편집</button>
                <button class="deal-tag-opt" type="button"
                        style="width:100%;border:0;background:transparent;text-align:left;font:inherit"
                        data-action="quote-download" data-quote-id="${escapeHtml(q.id)}">견적서 다운로드</button>
                <button class="deal-tag-opt" type="button"
                        style="width:100%;border:0;background:transparent;text-align:left;font:inherit"
                        data-action="quote-copy" data-quote-id="${escapeHtml(q.id)}">견적 복사</button>
                <button class="deal-tag-opt" type="button"
                        style="width:100%;border:0;background:transparent;text-align:left;font:inherit;color:#dc2626${q.status === 'draft' ? '' : ';opacity:.4;cursor:not-allowed'}"
                        ${q.status === 'draft' ? '' : 'disabled'}
                        title="${q.status === 'draft' ? '이 견적을 삭제합니다' : '작성됨 상태의 견적만 삭제할 수 있습니다'}"
                        data-action="quote-delete" data-quote-id="${escapeHtml(q.id)}">견적 삭제</button>
              </div>` : ''}
          </td>
        </tr>
      `;
  }).join('');
  return `
      <div class="deal-table-wrap">
        <table class="deal-quote-table">
          <thead>
            <tr>
              <th>견적일</th>
              <th>QUOTE CODE</th>
              <th>견적명</th>
              <th>상태</th>
              <th class="text-center">견적 유형</th>
              <th class="text-right">견적 금액</th>
              <th class="text-right">VAT 포함</th>
              <th class="text-right">마진율</th>
              <th class="text-center" style="width: 60px;">작업</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    `;
}

export function renderQuoteStatusChip(status: api.QuoteStatus): string {
  const labels: Record<api.QuoteStatus, string> = {
    draft:        '작성됨',
    sent:         '발송됨',
    accepted:     '채택됨',
    not_selected: '미채택',
    rejected:     '미채택',   // deprecated — 표시는 미채택으로 통합
    expired:      '만료',
  };
  return `<span class="quote-status quote-status-${escapeHtml(status)}">${escapeHtml(labels[status] ?? status)}</span>`;
}

// ══════════════════════════════════════════════════════════════════
// 견적 생성/수정 모달 — render (P9-UI Step 3b)
// ══════════════════════════════════════════════════════════════════
//
// hidden / shown 상태는 modal.open 으로 토글. close 버튼 / overlay 클릭으로 닫힘.
// 안의 input 값은 quoteModal state 에 보관. blur (change) 시 부분 refresh.
export function renderQuoteModal(
  m:            QuoteModalState,
  full:         api.DealFull,
  quoteSources: api.DealRfqSource[],
  quotePrices:  api.DealQuotePrice[] = [],
  itemSources:  SourcePool = { loading: false, stock: [], rfq: [] },
): string {
  if (!m.open) {
    return `<div class="deal-quote-modal hidden" aria-hidden="true"></div>`;
  }

  const isRental = m.quoteType === 'RENTAL';
  const totals = computeQuoteTotals(m);
  // 번들가 미입력 / 구성품 합계 불일치 → 저장 버튼 비활성 + 사유 표시.
  // alert 로 튕기지 않는 이유: 어느 줄을 고쳐야 하는지 보면서 맞춰야 한다.
  //
  // ★ 임대는 **판매 사유를 쓰지 않는다.** 판매 사유는 '서버 1대 판매가 미입력'
  //   처럼 판매단가 칸을 전제로 하는데, 임대에는 그 칸이 없다(단가를 계산이
  //   만든다). 섞으면 임대 견적이 영원히 저장되지 않는다.
  const blockReasons = isRental
    ? rentalBlockReasons(m.items, parseNum(m.fxRate) ?? 0, m.presets)
    : quoteBlockReasons(m.items);
  const editing = m.editId != null;

  return `
      <div class="deal-quote-modal" role="dialog" aria-labelledby="quote-modal-title">
        <div class="deal-quote-modal-overlay" data-action="quote-modal-overlay"></div>
        <div class="deal-quote-modal-dialog">
          <header class="deal-quote-modal-header">
            <div>
              <h2 id="quote-modal-title">${isRental ? '임대 ' : ''}${editing ? '견적 수정' : '견적 생성'}</h2>
              <p class="text-muted text-sm">${isRental
                ? (editing ? '이 임대 견적의 기간·계산식을 수정합니다.' : '기간과 계산식으로 월 임대단가를 계산합니다.')
                : (editing ? '이 견적의 항목·가격을 수정합니다.' : '현재 구성으로 견적을 생성합니다.')}</p>
            </div>

            <button class="btn btn-ghost btn-sm" type="button"
                    data-action="quote-modal-close"
                    aria-label="닫기">Close</button>
          </header>

          <div class="deal-quote-modal-body">
            ${renderQuoteModalMeta(m, full)}
            ${isRental
              ? renderQuoteModalRentalTable(m, itemSources)
              : renderQuoteModalItemsTable(m, totals, quoteSources, quotePrices, itemSources)}
          </div>

          <!-- 합계 카드는 표 밖으로 빼서 하단에 **고정**한다 (2026-08-24 현민).
               body 안에 두면 품목이 많을 때 스크롤 아래로 밀려, 얼마짜리
               견적인지 보려면 표를 끝까지 내려야 했다. 표만 스크롤되고
               합계·저장은 항상 보이게 한다. -->
          <div class="deal-quote-modal-summary-region">
            ${isRental
              ? renderRentalModalTotals(m)
              : renderQuoteModalTotals(totals)}
          </div>

          <footer class="deal-quote-modal-footer">
            <p class="text-muted text-sm">
              ${editing
                ? '저장하면 이 견적의 항목과 금액이 갱신됩니다.'
                : '저장하면 견적 섹션에 row 가 추가되고 상태가 <strong>견적 작성 완료</strong>로 변경됩니다.'}
            </p>
            ${m.errorMessage ? `<div class="alert alert-error">${escapeHtml(m.errorMessage)}</div>` : ''}
            ${blockReasons.length > 0 ? `
              <div class="deal-quote-block" role="alert">
                ${blockReasons.map((r) => `<div>${escapeHtml(r)}</div>`).join('')}
              </div>` : ''}
            <div class="deal-quote-modal-actions">
              <button class="btn btn-ghost" type="button"
                      data-action="quote-modal-close" ${m.saving ? 'disabled' : ''}>취소</button>
              <button class="btn btn-primary" type="button"
                      data-action="quote-modal-save"
                      ${m.saving || blockReasons.length > 0 ? 'disabled' : ''}>
                ${m.saving ? '저장 중…' : (editing ? '수정 저장' : '저장')}
              </button>
            </div>
          </footer>
        </div>
      </div>
    `;
}

// 메타 3 박스 (거래처 / 구성명 / 환율) — 거래처는 readonly 표시
function renderQuoteModalMeta(m: QuoteModalState, full: api.DealFull): string {
  return `
      <div class="deal-quote-modal-meta">
        <div class="deal-quote-modal-meta-box">
          <div class="deal-quote-modal-meta-label">거래처</div>
          <div class="deal-quote-modal-meta-value">${escapeHtml(full.customer_name ?? '-')}</div>
        </div>
        <label class="deal-quote-modal-meta-box">
          <div class="deal-quote-modal-meta-label">구성명</div>
          <input class="deal-quote-modal-meta-input" type="text"
                 data-action="quote-meta-field" data-meta-field="name"
                 value="${escapeHtml(m.name)}">
        </label>
        <label class="deal-quote-modal-meta-box">
          <div class="deal-quote-modal-meta-label">환율 (KRW/USD)</div>
          <input class="deal-quote-modal-meta-input text-right" type="number"
                 step="0.01" min="0"
                 data-action="quote-meta-field" data-meta-field="fxRate"
                 value="${escapeHtml(m.fxRate)}">
        </label>
      </div>
    `;
}

// 항목 표
function renderQuoteModalItemsTable(
  m:             QuoteModalState,
  totals:        QuoteTotals,
  // 소스 선택은 itemSources(보유재고+RFQ 통합 pool)로 옮겨졌다. 호출부 인자 순서를
  // 유지하려고 자리만 남겨 둔다 — 다음 정리 때 호출부와 함께 제거.
  _quoteSources: api.DealRfqSource[],
  quotePrices:   api.DealQuotePrice[] = [],
  itemSources:   SourcePool = { loading: false, stock: [], rfq: [] },
): string {
  if (m.items.length === 0) {
    return `
        <div class="deal-quote-modal-empty text-muted">
          품목이 없습니다. 모달을 닫고 품목을 선택하세요.
        </div>
      `;
  }

  const fx = parseNum(m.fxRate) ?? 0;
  // 매입단가가 하나라도 입력됐으면 나머지 미입력 칸을 0 으로 본다(서안드레 스펙).
  // 하나도 없으면 예전처럼 전부 '확인 필요'. 판정은 행마다가 아니라 견적 단위다.
  const zeroCost = anyCostEntered(m.items);
  const expandedServers = m.expandedServers;
  const rowsHtml = m.items.map((row, idx) => {
    const isServer = row.lineType === 'server';
    const isChild  = row.parentDealItemId != null;
    // 접힌 서버의 구성품은 그리지 않는다(QUOTE-009 — 기본 접힘).
    if (isChild && !expandedServers.has(String(row.parentDealItemId))) return '';

    const qty      = parseInt0(row.quantity)       ?? 0;
    // 서버 헤더행의 판매단가는 자기 값이 아니라 **자식 합(자동)/수동값**에서 온다.
    //   (매입단가가 serverRowCostKrw 로 유도되는 것과 대칭.)
    const sellUnit = isServer
      ? serverEffectiveSellKrw(row, m.items)
      : parseInt0(row.unit_price_krw);       // 판매단가(개당) — 직접 입력
    // 서버 헤더행의 매입단가는 자기 값이 아니라 **자식 합계**에서 유도된다.
    const costKrw  = isServer
      ? serverRowCostKrw(row, m.items, fx, zeroCost)
      : rowCostKrw(row, fx, zeroCost);
    // 공용 로직(computeRowMargin). zeroCost 면 미입력 칸이 0 원으로 계산된다.
    // 기본 구성 행은 견적 전체 판정과 무관하게 **항상** 0 을 확정 원가로 본다 —
    // 섀시 값에 포함이라 0 이 맞고, anyCostEntered 에서는 빠져 있기 때문이다.
    const { costPresent, lineCost, lineSell, marginAmt, marginRate } =
      computeRowMargin(costKrw, sellUnit, qty, zeroCost || row.isBasePart);
    const marginAmtLabel  = marginAmt != null ? fmtKrw(marginAmt) : '-';        // 없음/0 → '-'
    const marginRateLabel = marginRate != null ? marginRate.toFixed(1) + '%' : '확인 필요';
    const marginCls = !costPresent ? 'text-muted'
      : (marginAmt != null && marginAmt < 0 ? 'text-danger' : 'text-success');

    // 소스 드롭다운 — **재고 + RFQ 통합 pool**(2026-08-04).
    //   딜 품목행·서버 구성품과 같은 후보·같은 라벨을 쓴다. 예전엔 여기만 RFQ 만
    //   보여줘서, 딜에서 재고를 골라 놓고 견적에 오면 그 선택이 후보에 없었다.
    //   후보 목록은 아래 sourceSelectHtml 이 pool 에서 직접 뽑는다 —
    //   여기서 sourceOptions 를 따로 부를 필요가 없다.
    const priceMatches = quotePrices.filter((p) => p.option_part === row.option_part);

    const hasItem = !!(row.option_part || row.description);
    // 손으로 추가한 행(딜/구성 출신 아님: id·deal_item_id 모두 null)은
    //   값이 채워져도 계속 input 을 유지한다 — 딜 품목만 고정 텍스트로 굳는다.
    const isHandAdded = row.id == null && row.deal_item_id == null;
    const partCell = isServer     ? serverHeaderPartCell(row, expandedServers)
      : isChild                   ? serverChildPartCell(row)
      : (hasItem && !isHandAdded)                   ? `<div class="deal-quote-item-part">${
            [row.option_part, row.description, row.condition]
              .filter(Boolean).map((x) => escapeHtml(String(x))).join(' ')}${basePartChip(row.isBasePart)}${regChip(row.isUnregistered, row.isPending)}</div>
           ${row.category ? `<div class="deal-quote-item-cat">${escapeHtml(row.category)}</div>` : ''}`
      : `<input class="input deal-quote-newpart" type="text" placeholder="파트넘버"
                  data-action="quote-row-field" data-row-field="optionPart" data-row-index="${idx}"
                  value="${escapeHtml(row.option_part ?? '')}">
           <input class="input deal-quote-newdesc" type="text" placeholder="품목명(선택)"
                  data-action="quote-row-field" data-row-field="description" data-row-index="${idx}"
                  value="${escapeHtml(row.description ?? '')}">`;

    // 서버 헤더행: 소스·매입단가는 정확히 `-`, 수량은 읽기 전용 대수 (QUOTE-008/009).
    // 기본 구성 품목은 매입 소스를 고르지 않는다(마이그 174) — 섀시 값에 포함.
    //   같은 행의 매입단가 칸이 비활성 input 이므로 소스도 비활성 select 로 맞춘다.
    const sourceCell = isServer
      ? '<span class="deal-quote-static">-</span>'
      : sourceSelectHtml({
          pool:     itemSources,
          part:     row.option_part ?? '',
          selected: selectedSourceValue(row.sourceType, row.sourceRefId),
          attrs:    row.isBasePart
            ? 'disabled title="기본 구성 품목은 섀시 값에 포함되어 소스를 고르지 않습니다."'
            : `data-action="quote-source-change" data-row-index="${idx}"`,
          cls:      'input deal-quote-source-select',
        });

    const qtyCell = isServer || isChild
      ? `<span class="deal-quote-static">${escapeHtml(row.quantity)}EA</span>`
      : `<input class="input text-right deal-quote-qty" type="number" min="1"
                data-action="quote-row-field" data-row-field="quantity" data-row-index="${idx}"
                value="${escapeHtml(row.quantity)}">`;

    const costCell = isServer
      ? '<span class="deal-quote-static">-</span>'
      : `<input class="input text-right deal-quote-price" type="text" inputmode="numeric"
                data-action="quote-row-field" data-row-field="costKrw" data-row-index="${idx}"
                value="${escapeHtml(costKrw != null ? fmtThousands(costKrw) : '')}" placeholder="0"
                ${row.isBasePart ? 'disabled title="기본 구성 품목은 섀시 값에 포함되어 매입원가가 0입니다."' : ''}>`;

    // 레퍼런스는 전용 컬럼이다(§4.10.42).
    //
    // 2026-08 (서안드레): **행 종류로 가리지 않는다.** 예전엔 서버 그룹(헤더행·
    // 구성품)을 통째로 빈칸으로 뒀다 — "구성품은 번들가 안에서 쪼갠 숫자라
    // 파트별 과거 견적가가 소음"이라는 이유였다. 그런데 저장된 견적을 다시
    // 여는 보기 모달은 같은 행에 레퍼런스를 **그대로 보여주고 있었다**.
    // 같은 견적이 만들 때와 열어볼 때 다르게 보이는 게 더 큰 문제였고,
    // 값을 넣는 시점에 근거가 필요하다는 게 현장 요구다.
    //
    // 그래서 보기 모달과 같은 규칙(무조건 렌더)으로 맞춘다. 파트넘버가 없는
    // 서버 헤더행은 매칭이 0 건이라 자연히 '없음' 이 되므로 따로 가릴 것이 없다.
    const refCell = renderQuoteReferenceCell(priceMatches, idx, m.refPopRow);

    // 접힌 서버의 자식은 그려지지 않으므로 "보이는 자식" 기준으로 프레임을 닫는다.
    const rowCls = serverFrameClass(m.items, idx,
      (r) => r.parentDealItemId == null || expandedServers.has(String(r.parentDealItemId)));

    return `
        <tr class="${rowCls}">
          <td class="deal-quote-item-cell">${partCell}${rowShorthandWarn(row, m.items)}</td>
          <td>${sourceCell}</td>
          <td class="text-right">${qtyCell}</td>
          <td class="text-right">${costCell}</td>
          <td class="text-right deal-quote-costsum">${escapeHtml(costPresent ? fmtKrw(lineCost) : '확인 필요')}</td>
          <td class="deal-quote-ref-cell">${refCell}</td>
          <td class="text-right">
            <input class="input text-right deal-quote-sell" type="text" inputmode="numeric"
                   data-action="quote-row-field" data-row-field="sellUnit" data-row-index="${idx}"
                   value="${escapeHtml(sellUnit != null ? fmtThousands(sellUnit) : '')}"
                   placeholder="0">
          </td>
          <td class="text-right deal-quote-sellsum">${escapeHtml(lineSell != null ? fmtKrw(lineSell) : '-')}</td>
          <td class="text-right deal-quote-marginamt ${marginCls}">${escapeHtml(marginAmtLabel)}</td>
          <td class="text-right deal-quote-marginval ${marginCls}">${escapeHtml(marginRateLabel)}</td>
          <td class="text-center">${isChild ? '' : `
            <button class="btn btn-ghost btn-sm text-danger deal-quote-del" type="button"
                    data-action="quote-item-delete" data-row-index="${idx}">삭제</button>`}
          </td>
        </tr>
      `;
  }).join('');

  return `
      <table class="deal-quote-modal-table deal-quote-modal-table-v2">
        <thead>
          <tr>
            <th>품목</th>
            <th>소스</th>
            <th class="text-right">수량</th>
            <th class="text-right">매입단가</th>
            <th class="text-right">매입단가 합계</th>
            <th>레퍼런스</th>
            <th class="text-right">판매단가</th>
            <th class="text-right">판매단가 합계</th>
            <th class="text-right">마진 금액</th>
            <th class="text-right">마진율</th>
            <th class="text-center">작업</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot>
          <tr class="deal-quote-modal-table-total-row">
            <td colspan="4" class="text-right text-muted">합계</td>
            <td class="text-right">${escapeHtml(totals.costComplete && totals.costBasisKrw > 0 ? fmtKrw(totals.costBasisKrw) : '확인 필요')}</td>
            <td></td>
            <td></td>
            <td class="text-right"><strong>${escapeHtml(fmtKrw(totals.totalKrw || null))}</strong></td>
            <td class="text-right">${escapeHtml(totals.marginKrw != null ? fmtKrw(totals.marginKrw) : '확인 필요')}</td>
            <td class="text-right">${escapeHtml(totals.marginRate != null ? fmtPct(totals.marginRate) : '확인 필요')}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      <button class="btn btn-ghost btn-sm deal-quote-additem" type="button" data-action="quote-item-add">+ 항목 추가</button>
    `;
}

// 하단 카드 4 (매입 합계 / 판매 합계 / VAT 포함 판매금액 / 전체 마진율)
function renderQuoteModalTotals(totals: QuoteTotals): string {
  const marginCls = totals.marginRate == null
    ? 'text-muted'
    : totals.marginRate < 0 ? 'text-danger' : 'text-success';
  // 매입단가가 하나라도 비면 매입 합계·전체 마진율은 '확인 필요'. 판매·VAT 는 항상 계산.
  const costLabel      = totals.costComplete ? fmtKrw(totals.costBasisKrw) : '확인 필요';
  const marginAmtLabel = totals.marginKrw  == null ? '확인 필요' : fmtKrw(totals.marginKrw);
  const marginLabel    = totals.marginRate == null ? '확인 필요' : fmtPct(totals.marginRate);

  return `
      <div class="deal-quote-modal-summary">
        <div class="deal-quote-modal-summary-box">
          <div class="deal-quote-modal-summary-label">매입 합계 (KRW)</div>
          <div class="deal-quote-modal-summary-value">${escapeHtml(costLabel)}</div>
        </div>
        <div class="deal-quote-modal-summary-box">
          <div class="deal-quote-modal-summary-label">판매 합계 (KRW)</div>
          <div class="deal-quote-modal-summary-value">${escapeHtml(fmtKrw(totals.totalKrw || null))}</div>
        </div>
        <div class="deal-quote-modal-summary-box">
          <div class="deal-quote-modal-summary-label">VAT 포함 판매금액 (KRW)</div>
          <div class="deal-quote-modal-summary-value">${escapeHtml(fmtKrw(totals.amountWithVat || null))}</div>
        </div>
        <div class="deal-quote-modal-summary-box">
          <div class="deal-quote-modal-summary-label">마진 금액 (KRW)</div>
          <div class="deal-quote-modal-summary-value ${marginCls}">${escapeHtml(marginAmtLabel)}</div>
        </div>
        <div class="deal-quote-modal-summary-box">
          <div class="deal-quote-modal-summary-label">전체 마진율</div>
          <div class="deal-quote-modal-summary-value ${marginCls}">${escapeHtml(marginLabel)}</div>
        </div>
      </div>
    `;
}

// ══════════════════════════════════════════════════════════════════
// 견적 보기 모달 — render (P9-UI Step 3c)
// ══════════════════════════════════════════════════════════════════
//
// 견적 행의 [보기] 클릭 시 모달 표시. fetchQuoteFull 결과를 보여줌.
// 메타 + items 표 + 합계. 편집 X (조회 전용).
export function renderQuoteViewModal(
  v:            QuoteViewModalState,
  quoteSources: api.DealRfqSource[],
  quotePrices:  api.DealQuotePrice[] = [],
  liveFx:       number = 0,
): string {
  if (!v.open) {
    return `<div class="deal-quote-modal hidden" aria-hidden="true"></div>`;
  }

  let bodyHtml: string;
  let summaryHtml = '';   // 고정 합계 영역 — 데이터가 있을 때만 채운다.
  if (v.loading) {
    bodyHtml = `<p class="text-muted text-center" style="padding: 32px;">불러오는 중…</p>`;
  } else if (v.error) {
    bodyHtml = `<div class="alert alert-error">${escapeHtml(v.error)}</div>`;
  } else if (v.data) {
    const parts = renderQuoteViewBody(v.data, quoteSources, quotePrices, liveFx, v.refPopRow);
    bodyHtml = parts.body;
    summaryHtml = parts.summary;
  } else {
    bodyHtml = `<p class="text-muted text-center" style="padding: 32px;">데이터 없음</p>`;
  }

  const title = v.data ? `견적 — ${escapeHtml(v.data.quote_no)}` : '견적 보기';

  // 상태별 액션 (기획): 작성됨→발송·수정 / 발송됨→채택 / 발송됨·채택됨→반려 / 항상 닫기
  const q   = v.data;
  const st  = q?.status;
  const qid = q ? escapeHtml(q.id) : '';
  const sendBtn = st === 'draft'
    ? `<button class="btn btn-secondary" type="button" data-action="send-quote" data-quote-id="${qid}">견적 발송</button>` : '';
  const editBtn = (st === 'draft' || st === 'sent')
    ? `<button class="btn btn-ghost" type="button" data-action="quote-edit" data-quote-id="${qid}">견적 수정</button>` : '';
  const acceptBtn = st === 'sent'
    ? `<button class="btn btn-primary" type="button" data-action="accept-quote" data-quote-id="${qid}">판매 등록</button>` : '';
  const rejectBtn = (st === 'sent' || st === 'accepted')
    ? `<button class="btn btn-ghost text-danger" type="button" data-action="reject-quote" data-quote-id="${qid}">견적 반려</button>` : '';

  // 닫기는 헤더 우측 상단 X 하나로 통일. 푸터는 액션 버튼이 있을 때만 표시.
  const actions = `${sendBtn}${acceptBtn}${rejectBtn}${editBtn}`;

  return `
      <div class="deal-quote-modal" role="dialog">
        <div class="deal-quote-modal-overlay" data-action="quote-view-overlay"></div>
        <div class="deal-quote-modal-dialog">
          <header class="deal-quote-modal-header">
            <div>
              <h2>${title}</h2>
              ${v.data ? `<p class="text-muted text-sm">${escapeHtml(v.data.name)}</p>` : ''}
            </div>
            <button class="deal-rfq-modal-close" type="button"
                    data-action="quote-view-close" aria-label="닫기">✕</button>
          </header>

          <div class="deal-quote-modal-body">${bodyHtml}</div>

          <!-- 합계 카드는 표 밖 하단 고정 (생성 모달과 같은 규칙, 2026-08-24).
               품목이 많아도 합계·액션이 항상 보인다. -->
          ${summaryHtml
            ? `<div class="deal-quote-modal-summary-region">${summaryHtml}</div>`
            : ''}

          ${actions.trim()
            ? `<footer class="deal-quote-modal-footer">
                 <div class="deal-quote-modal-actions">${actions}</div>
               </footer>`
            : ''}
        </div>
      </div>
    `;
}

// 견적 본문 — 메타 + items 표 + 합계
/**
 * 서버 헤더 행의 '매입금액 합계' — **자식 구성품들의 (매입단가 × 수량) 합**.
 *
 * 서버 헤더행은 자체 cost_basis_amount 가 없어(매입원가는 구성품에 있다) 그대로
 * 계산하면 ₩0 으로 나온다. 견적을 다시 열 때마다 서버 매입금액이 0 으로 보이던
 * 것이 이 때문이다(서안드레 2026-08-22, 판매·임대 공통).
 *
 * 자식은 parent_deal_item_id === 헤더의 deal_item_id 로 잇는다(마이그 131).
 * 자식 원가 환산은 표 본문과 같은 규칙(원화 그대로 / 외화 × costFx)을 쓴다.
 *
 * 반환: 헤더 deal_item_id → { sum, allPresent }. allPresent 는 그 서버의 매입
 * 원가가 하나라도 빠졌는지 — 빠졌으면 헤더 합계를 '확인 필요' 로 둔다.
 */
function serverChildCostSums(
  items: readonly api.QuoteItem[],
  costFx: number,
): Map<string, { sum: number; allPresent: boolean }> {
  // 매입원가가 하나라도 입력됐으면 빈 칸을 0 으로 본다 — 표 본문의 zeroCost 와 동일.
  const zeroCost = items.some((it) => (it.line_type ?? 'part') !== 'server'
                                   && !(it.is_base_part ?? false)
                                   && it.cost_basis_amount != null);
  const out = new Map<string, { sum: number; allPresent: boolean }>();
  for (const it of items) {
    const parent = it.parent_deal_item_id;
    if (parent == null) continue;
    const key = String(parent);
    const acc = out.get(key) ?? { sum: 0, allPresent: true };
    const qty = it.quantity ?? 0;
    const costKrw = it.cost_basis_amount != null
      ? (it.cost_basis_currency === 'KRW'
          ? Math.round(it.cost_basis_amount)
          : (costFx > 0 ? Math.round(it.cost_basis_amount * costFx) : null))
      : (zeroCost || (it.is_base_part ?? false) ? 0 : null);
    if (costKrw == null) acc.allPresent = false;   // 환산 불가·미입력 — 합계 미확정
    else acc.sum += costKrw * qty;
    out.set(key, acc);
  }
  return out;
}

/**
 * 매입금액 합계 셀 문구.
 *   서버 헤더행: 자식 합(serverChildCostSums). 하나라도 원가가 없으면 '확인 필요'.
 *   그 밖의 행:  이 행의 lineCost(costPresent 로 판정).
 */
function serverCostSumCell(
  isServerHeader: boolean,
  dealItemId:     string | null | undefined,
  srvCost:        Map<string, { sum: number; allPresent: boolean }>,
  costPresent:    boolean,
  lineCost:       number | null,
): string {
  if (isServerHeader) {
    const agg = dealItemId != null ? srvCost.get(String(dealItemId)) : undefined;
    if (!agg || !agg.allPresent) return '확인 필요';
    return '₩ ' + agg.sum.toLocaleString('ko-KR');
  }
  return costPresent && lineCost != null ? '₩ ' + lineCost.toLocaleString('ko-KR') : '확인 필요';
}

/** 보기 모달 본문 — body(스크롤)와 summary(고정 합계)를 나눠 반환한다. */
function renderQuoteViewBody(
  q:            api.QuoteFull,
  quoteSources: api.DealRfqSource[],
  quotePrices:  api.DealQuotePrice[] = [],
  liveFx:       number = 0,
  refPopRow:    number | null = null,
): { body: string; summary: string } {
  /** 저장된 환율 — 견적을 만들 때 못박은 값. 메타 표시와 임대 계산이 쓴다. */
  const fx = q.fx_rate_krw_per_usd ?? 0;
  /**
   * 매입단가·마진을 환산할 환율 — **오늘 환율**을 우선한다 (2026-08-24 현민).
   *
   * USD 매입원가의 원화 값은 결제까지 계속 움직인다. 저장된 환율로 보여주면
   * '지금 이 견적을 이 판매가로 내면 마진이 얼마인가' 라는 질문에 옛 답을
   * 준다. 이 모달은 그 판단을 하려고 여는 화면이다.
   *
   * 고객에게 나간 금액은 흔들리지 않는다 — 판매단가는 KRW(unit_price_krw)로
   * 저장되고 견적서 엑셀에는 원가·마진 칸이 아예 없다.
   *
   * ★ 임대 보기 표도 같이 쓴다. 거기서 환율이 닿는 곳은 매입단가 칸뿐이고
   *   임대 단가·마진율은 저장값을 그대로 읽는다(renderQuoteViewRentalTable
   *   주석). 고객 금액이 원가에서 계산되는 것은 **생성·수정 모달**의
   *   computeRentalLine 이고, 그쪽은 저장된 환율로 연다.
   */
  const costFx = liveFx > 0 ? liveFx : fx;

  // 메타 박스 (4)
  const metaHtml = `
      <div class="deal-quote-modal-meta" style="grid-template-columns: repeat(4, 1fr);">
        <div class="deal-quote-modal-meta-box">
          <div class="deal-quote-modal-meta-label">상태</div>
          <div class="deal-quote-modal-meta-value">${renderQuoteStatusChip(q.status)}</div>
        </div>
        <div class="deal-quote-modal-meta-box">
          <div class="deal-quote-modal-meta-label">결과</div>
          <div class="deal-quote-modal-meta-value">${escapeHtml(q.result ?? '-')}</div>
        </div>
        <div class="deal-quote-modal-meta-box">
          <div class="deal-quote-modal-meta-label">환율 (KRW/USD)</div>
          <!-- 매입단가·마진은 오늘 환율로 환산한다 → 오늘 환율을 주(main)로,
               견적 작성 시점의 저장 환율은 참고로 아래에 둔다.
               RENTAL·환산불가·동일 환율이면 저장 환율만 보인다. -->
          ${q.quote_type !== 'RENTAL' && costFx > 0 && costFx !== fx
            ? `<div class="deal-quote-modal-meta-value">${costFx.toLocaleString('ko-KR')} (오늘)</div>
               <div class="deal-quote-modal-meta-sub">작성 시점 ${fx > 0 ? fx.toLocaleString('ko-KR') : '-'}</div>`
            : `<div class="deal-quote-modal-meta-value">${fx > 0 ? fx.toLocaleString('ko-KR') : '-'}</div>`}
        </div>
        <div class="deal-quote-modal-meta-box">
          <div class="deal-quote-modal-meta-label">작성일</div>
          <div class="deal-quote-modal-meta-value">${escapeHtml(fmtDate(q.created_at))}</div>
        </div>
      </div>
    `;

  // items 표 — 보기 모드: 소스·매입단가 편집 가능(즉시 저장), 판매단가 read-only.
  const items = (q.items ?? []).filter((it) => it.is_active !== false);

  // 비고 — 판매·임대 공용이라 표보다 먼저 만들어 둔다(임대 분기가 이 값을 쓴다).
  const notesHtml = q.notes
    ? `<div style="margin-top: 12px; padding: 12px; background: #fff; border: 1px solid var(--border, #e5e7eb); border-radius: 8px;">
           <div class="deal-quote-modal-meta-label">비고</div>
           <div class="text-sm" style="margin-top: 4px; white-space: pre-wrap;">${escapeHtml(q.notes)}</div>
         </div>`
    : '';

  // ★ 임대 견적은 표도 합계도 통째로 다르다 — 분기가 아니라 **별도 함수**로 그린다.
  //   판매 표와 겹치는 열은 앞의 넷(품목·소스·수량·매입단가)뿐이고, 임대에는
  //   레퍼런스·마진 금액 열이 없다. 한 표에 끼워 넣으면 판매 견적 쪽 colspan 과
  //   서버 프레임 계산이 조용히 깨진다(생성 모달에서 내린 판단과 같다).
  if (q.quote_type === 'RENTAL') {
    // 임대 표에서 환율이 닿는 곳은 **매입단가 칸 하나**다. 임대 단가는 저장된
    //   unit_price_krw 를 그대로 읽고 마진율도 저장된 임대총원가 기준이라,
    //   오늘 환율을 써도 고객 금액은 움직이지 않는다. 판매와 같게 맞춘다.
    return {
      body:    metaHtml + renderQuoteViewRentalTable(items, quoteSources, costFx) + notesHtml,
      summary: renderQuoteViewRentalTotals(items),
    };
  }
  // 합계 재계산 — 편집 즉시 반영을 위해 서버 캐시가 아닌 현재 item 값으로 집계.
  const rms: RowMargin[] = [];
  let itemsHtml: string;
  if (items.length === 0) {
    itemsHtml = `<p class="text-muted text-center" style="padding: 16px;">견적 항목이 없습니다.</p>`;
  } else {
    // serverFrameClass 는 모달 행 모델(lineType/parentDealItemId/deal_item_id)을
    // 기대한다. quote_item 의 스네이크 케이스를 그 모양으로 한 번만 맞춰 둔다.
    const frameRows = items.map((it) => ({
      lineType:         it.line_type ?? 'part',
      parentDealItemId: it.parent_deal_item_id ?? null,
      deal_item_id:     it.deal_item_id ?? null,
    }));
    // 생성 모달과 같은 규칙. 여기 items 는 quote_item(스네이크) 이라
    // anyCostEntered(모달 행 모델)를 그대로 못 쓰고 같은 판정을 편다.
    const zeroCost = items.some((it) => (it.line_type ?? 'part') !== 'server'
                                     && !(it.is_base_part ?? false)
                                     && it.cost_basis_amount != null);
    // 서버 헤더행의 매입금액 합계 = 자식 합. 헤더는 자체 원가가 없다.
    const srvCost = serverChildCostSums(items, costFx);
    const rows = items.map((it, idx) => {
      const qty     = it.quantity      ?? 0;
      const unitKrw = it.unit_price_krw ?? 0;
      const costKrw = it.cost_basis_amount != null
        ? (it.cost_basis_currency === 'KRW'
            ? Math.round(it.cost_basis_amount)
            : (costFx > 0 ? Math.round(it.cost_basis_amount * costFx) : null))   // 환산 불가는 null 유지
        : (zeroCost ? 0 : null);
      // 공용 로직(computeRowMargin). zeroCost 면 미입력 칸이 0 원으로 계산된다.
      // 기본 구성 행은 생성 모달과 같이 항상 0 확정.
      const rm = computeRowMargin(costKrw, unitKrw, qty,
                                  zeroCost || (it.is_base_part ?? false));
      rms.push(rm);
      const costPresent = rm.costPresent;
      const lineSell    = qty * unitKrw;         // 판매단가 합계(표시용, unitKrw 항상 존재)
      const lineCost    = rm.lineCost;           // 매입단가 합계 (없음/0 → null)
      const marginCls = !costPresent ? 'text-muted'
        : (rm.marginAmt != null && rm.marginAmt < 0 ? 'text-danger' : 'text-success');
      const marginAmtLabel  = rm.marginAmt  != null ? '₩ ' + rm.marginAmt.toLocaleString('ko-KR') : '-';
      const marginRateLabel = rm.marginRate != null ? rm.marginRate.toFixed(1) + '%' : '확인 필요';

      // 소스 드롭다운 — 같은 파트넘버의 딜 RFQ 후보 (편집 가능, 서버가 파트별 15개로 컷)
      const matches = quoteSources.filter((s) => s.option_part === it.option_part);
      const priceMatches = quotePrices.filter((p) => p.option_part === it.option_part);
      const srcOpts = ['<option value="">선택 없음</option>',
        ...matches.map((s) => {
          const label = quoteSourceLabel(s);
          const sel = it.source_ref_id === String(s.rfq_id) ? 'selected' : '';
          return `<option value="${s.rfq_id}" ${sel}>${escapeHtml(label)}</option>`;
        })].join('');

      const partLine = [it.option_part, it.description, it.requested_condition]
        .filter(Boolean).map((x) => escapeHtml(String(x))).join(' ');

      // 보기 모달도 서버 그룹을 같은 모양으로 두른다 — 저장된 견적을 다시 열었을 때
      // 구성품과 개별 품목이 섞여 보이면 안 된다. 여기서는 접기가 없어(전부 표시)
      // 프레임은 항상 마지막 구성품이 닫는다.
      const isSrvHdr = it.line_type === 'server';
      const isSrvKid = it.parent_deal_item_id != null;
      const rowCls   = serverFrameClass(frameRows, idx, () => true);
      const partCell = isSrvHdr
        ? `<div class="deal-quote-server-model">${escapeHtml(withServerSuffix(it.description) || '서버')}</div>
           <div class="deal-quote-server-cond">${escapeHtml(String(it.server_quantity ?? 1))}대</div>`
        : isSrvKid
          ? `<div class="deal-quote-child-head">
               <span class="deal-quote-child-cat">${escapeHtml(it.category ?? '-')}</span>
               <span class="deal-quote-child-part">${escapeHtml(it.option_part ?? '파트넘버 미지정')}</span>
               ${basePartChip(it.is_base_part ?? false)}${regChip(it.is_unregistered, it.is_pending)}
             </div>
             <div class="deal-quote-child-desc">${escapeHtml(it.description ?? '')}</div>`
          : `<div class="deal-quote-item-part">${partLine || '-'}${basePartChip(it.is_base_part ?? false)}${regChip(it.is_unregistered, it.is_pending)}</div>
             ${it.category ? `<div class="deal-quote-item-cat">${escapeHtml(it.category)}</div>` : ''}`;

      return `
          <tr class="${rowCls}">
            <td class="deal-quote-item-cell">${partCell}${rowShorthandWarn(it, items)}</td>
            <td>
              <select class="input deal-quote-source-select"
                ${it.is_base_part
                  ? 'disabled title="기본 구성 품목은 섀시 값에 포함되어 소스를 고르지 않습니다."'
                  : `data-action="quote-view-source-change" data-item-index="${idx}"
                     ${matches.length === 0 ? 'disabled title="이 품목의 RFQ 가격 없음"' : ''}`}>
                ${srcOpts}
              </select>
            </td>
            <td class="text-right">${qty || '-'}</td>
            <td class="text-right">
              <input class="input text-right deal-quote-price" type="text" inputmode="numeric"
                     data-action="quote-view-cost-change" data-item-index="${idx}"
                     value="${escapeHtml(costKrw != null ? fmtThousands(costKrw) : '')}" placeholder="0"
                     ${it.is_base_part ? 'disabled title="기본 구성 품목은 섀시 값에 포함되어 매입원가가 0입니다."' : ''}>
            </td>
            <td class="text-right deal-quote-costsum">${serverCostSumCell(
              isSrvHdr, it.deal_item_id, srvCost, costPresent, lineCost)}</td>
            <td class="deal-quote-ref-cell">${renderQuoteReferenceCell(priceMatches, idx, refPopRow)}</td>
            <td class="text-right text-sm">${lineSell > 0 ? '₩ ' + unitKrw.toLocaleString('ko-KR') : '-'}</td>
            <td class="text-right deal-quote-sellsum">${lineSell > 0 ? '₩ ' + lineSell.toLocaleString('ko-KR') : '-'}</td>
            <td class="text-right deal-quote-marginamt ${marginCls}">${marginAmtLabel}</td>
            <td class="text-right deal-quote-marginval ${marginCls}">${marginRateLabel}</td>
          </tr>
        `;
    }).join('');

    itemsHtml = `
        <table class="deal-quote-modal-table deal-quote-modal-table-v2">
          <thead>
            <tr>
              <th>품목</th>
              <th>소스</th>
              <th class="text-right">수량</th>
              <th class="text-right">매입단가</th>
              <th class="text-right">매입단가 합계</th>
              <th>레퍼런스</th>
              <th class="text-right">판매단가</th>
              <th class="text-right">판매단가 합계</th>
              <th class="text-right">마진 금액</th>
              <th class="text-right">마진율</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      `;
  }

  // 합계 카드 (5) — 매입 / 판매 / VAT / 마진 금액 / 전체 마진율. 공용 summarizeRows 로 집계.
  const totals = summarizeRows(rms, false);   // 보기 모달은 항상 VAT 별도(×1.1)
  const marginCls = totals.marginRate == null ? 'text-muted' : totals.marginRate < 0 ? 'text-danger' : 'text-success';
  const totalsHtml = `
      <div class="deal-quote-modal-summary">
        <div class="deal-quote-modal-summary-box">
          <div class="deal-quote-modal-summary-label">매입 합계 (KRW)</div>
          <div class="deal-quote-modal-summary-value">${totals.costComplete ? '₩ ' + totals.costBasisKrw.toLocaleString('ko-KR') : '확인 필요'}</div>
        </div>
        <div class="deal-quote-modal-summary-box">
          <div class="deal-quote-modal-summary-label">판매 합계 (KRW)</div>
          <div class="deal-quote-modal-summary-value">${totals.totalKrw > 0 ? '₩ ' + totals.totalKrw.toLocaleString('ko-KR') : '-'}</div>
        </div>
        <div class="deal-quote-modal-summary-box">
          <div class="deal-quote-modal-summary-label">VAT 포함 판매금액 (KRW)</div>
          <div class="deal-quote-modal-summary-value">${totals.totalKrw > 0 ? '₩ ' + totals.amountWithVat.toLocaleString('ko-KR') : '-'}</div>
        </div>
        <div class="deal-quote-modal-summary-box">
          <div class="deal-quote-modal-summary-label">마진 금액 (KRW)</div>
          <div class="deal-quote-modal-summary-value ${marginCls}">${totals.marginKrw != null ? '₩ ' + totals.marginKrw.toLocaleString('ko-KR') : '확인 필요'}</div>
        </div>
        <div class="deal-quote-modal-summary-box">
          <div class="deal-quote-modal-summary-label">전체 마진율</div>
          <div class="deal-quote-modal-summary-value ${marginCls}">${totals.marginRate != null ? totals.marginRate.toFixed(1) + '%' : '확인 필요'}</div>
        </div>
      </div>
    `;

  return { body: `${metaHtml}${itemsHtml}${notesHtml}`, summary: totalsHtml };
}



// ═══ 임대 견적 (마이그 178/179) ═════════════════════════════════════
//
// ★ 판매 표를 분기시키지 않고 **별도 표**로 그린다.
//   판매 표는 11열에 tfoot 의 colspan·서버 프레임 계산이 얽혀 있어서, 임대 열을
//   끼워 넣으면 판매 견적 쪽이 조용히 깨진다. 게다가 두 표는 보여줄 것이 다르다
//   — 판매는 '판매단가'를 사람이 넣고, 임대는 단가를 **계산이 만든다**.
//   같은 표에 두면 어느 칸이 입력이고 어느 칸이 결과인지 읽히지 않는다.
//   품목 셀·소스 셀 같은 공용 조각은 아래에서 그대로 재사용한다.

/**
 * 임대 항목 표 — 기획안(adminuxpreview) 열 구성을 따른다.
 *
 * 열: 품목 · 소스 · 수량 · 매입단가 · 매입금액 합계 · 임대총원가 ·
 *     임대 계산식 · 임대 기간 · 임대 단가 · 월 임대 금액 · 금액 합계 · 마진율 · 작업
 *
 * ★ 사람이 넣는 칸은 셋뿐이다 — 계산식 · 기간 · 임대 단가.
 *   나머지는 전부 계산 결과이고, 매입금액 합계는 소스·매입단가에서 유도된다.
 *
 * ★ '수동 단가' 열을 따로 두지 않는다. **임대 단가 칸 자체가 입력이다** —
 *   계산된 값이 미리 채워져 있고 사람이 덮어쓰면 그것이 시트 R열의 수동 단가가
 *   된다. 열을 나누면 "계산값이 얼마인데 내가 얼마로 바꾸는지" 가 두 칸으로
 *   흩어지고, 어느 쪽이 실제 적용값인지 매번 눈으로 대조해야 한다.
 *
 * ★ 임대 계산식 열은 기획안에 없다. 그 프로토타입이 프리셋 설계 이전 판이기
 *   때문이고(rental-pricing.ts 헤더 참조), 프리셋은 사람이 골라야 하므로 남긴다.
 */
function renderQuoteModalRentalTable(
  m:           QuoteModalState,
  itemSources: SourcePool = { loading: false, stock: [], rfq: [] },
): string {
  if (m.items.length === 0) {
    return `<div class="deal-quote-modal-empty text-muted">품목이 없습니다. 모달을 닫고 품목을 선택하세요.</div>`;
  }
  if (m.presetsLoading) {
    return `<div class="deal-quote-modal-empty text-muted">임대 계산식을 불러오는 중…</div>`;
  }
  if (m.presets.length === 0) {
    return `
      <div class="deal-quote-modal-empty text-danger">
        쓸 수 있는 임대 계산식(프리셋)이 없습니다.
        ${m.presetsError ? escapeHtml(m.presetsError) : '설정 → 임대 견적에서 프리셋을 먼저 만들어 주세요.'}
      </div>`;
  }

  const fx = parseNum(m.fxRate) ?? 0;
  const expandedServers = m.expandedServers;
  // ★ 판매 표와 **같은 판정**을 쓴다 — 매입단가가 하나라도 입력됐으면 나머지
  //   빈 칸을 0 으로 본다. 임대 라인의 매입금액 합계(rentalLinePurchaseKrw)가
  //   이미 anyCostEntered 로 계산되므로, 구성품 행을 다른 규칙으로 찍으면
  //   자식들의 합이 서버 행의 매입금액 합계와 어긋난다.
  const zeroCost = anyCostEntered(m.items);

  const rowsHtml = m.items.map((row, idx) => {
    const isServer = row.lineType === 'server';
    const isChild  = row.parentDealItemId != null;
    if (isChild && !expandedServers.has(String(row.parentDealItemId))) return '';

    // 품목이 없는 빈 행(+ 항목 추가)은 판매 표와 같이 파트넘버·품목명 입력을
    //   보여준다. 임대도 개별 품목을 손으로 추가할 수 있어야 한다(현민 2026-08-24).
    const hasItem = !!(row.option_part || row.description);
    // 손으로 추가한 행(딜/구성 출신 아님: id·deal_item_id 모두 null)은
    //   값이 채워져도 계속 input 을 유지한다 — 딜 품목만 고정 텍스트로 굳는다.
    const isHandAdded = row.id == null && row.deal_item_id == null;
    const partCell = isServer ? serverHeaderPartCell(row, expandedServers)
      : isChild               ? serverChildPartCell(row)
      : (hasItem && !isHandAdded)               ? `<div class="deal-quote-item-part">${
          [row.option_part, row.description].filter(Boolean)
            .map((x) => escapeHtml(String(x))).join(' ')}${basePartChip(row.isBasePart)}${regChip(row.isUnregistered, row.isPending)}</div>`
      : `<input class="input deal-quote-newpart" type="text" placeholder="파트넘버"
                  data-action="quote-row-field" data-row-field="optionPart" data-row-index="${idx}"
                  value="${escapeHtml(row.option_part ?? '')}">
           <input class="input deal-quote-newdesc" type="text" placeholder="품목명(선택)"
                  data-action="quote-row-field" data-row-field="description" data-row-index="${idx}"
                  value="${escapeHtml(row.description ?? '')}">`;

    const rowCls = serverFrameClass(m.items, idx,
      (r) => r.parentDealItemId == null || expandedServers.has(String(r.parentDealItemId)));

    const sourceCell = (dis: boolean) => sourceSelectHtml({
      pool: itemSources, part: row.option_part ?? '',
      selected: selectedSourceValue(row.sourceType, row.sourceRefId),
      attrs: dis
        ? 'disabled title="기본 구성 품목은 섀시 값에 포함되어 소스를 고르지 않습니다."'
        : `data-action="quote-source-change" data-row-index="${idx}"`,
      cls: 'input deal-quote-source-select',
    });
    const costInput = `<input class="input text-right deal-quote-price" type="text" inputmode="numeric"
             data-action="quote-row-field" data-row-field="costKrw" data-row-index="${idx}"
             value="${escapeHtml((() => { const c = rowCostKrw(row, fx, false); return c != null ? fmtThousands(c) : ''; })())}"
             placeholder="0"
             ${row.isBasePart ? 'disabled title="기본 구성 품목은 섀시 값에 포함되어 매입원가가 0입니다."' : ''}>`;

    // ★ 서버 구성품은 임대 입력을 받지 않는다. 매입원가의 재료일 뿐이고,
    //   계약 금액은 최상위 라인만으로 정해진다(CONFIG2-004 와 같은 규칙).
    //
    // ★ 다만 **매입금액 합계는 파트마다 찍는다** (서안드레 2026-08-20):
    //   "견적모달 매입금액 합계에서 수량이 곱해진 매입금액 합계 금액을
    //    파트마다 모두 출력". 예전에는 이 칸까지 안내 문구가 덮고 있어서,
    //   3EA 짜리 부품이 실제로 얼마를 싣고 있는지 표에서 볼 수 없었다.
    //   판매 표는 이미 구성품마다 이 칸을 찍는다 — 두 표를 같은 규칙으로 맞춘다.
    if (isChild) {
      const childUnit = rowCostKrw(row, fx, zeroCost || row.isBasePart);
      const childLine = childUnit != null ? childUnit * (parseInt0(row.quantity) ?? 0) : null;
      return `
        <tr class="${rowCls}">
          <td class="deal-quote-item-cell">${partCell}${rowShorthandWarn(row, m.items)}</td>
          <td>${sourceCell(row.isBasePart)}</td>
          <td class="text-right"><span class="deal-quote-static">${escapeHtml(row.quantity)}EA</span></td>
          <td class="text-right">${costInput}</td>
          <td class="text-right deal-quote-costsum">${
            escapeHtml(childLine != null ? fmtKrw(childLine) : '확인 필요')}</td>
          <!-- 안내 문구 제거(2026-08-24 현민): 매입금액 합계가 이미 서버에 합산됨을
               보여주므로 회색 텍스트가 오히려 혼란을 줬다. 빈 칸으로 둔다(colspan 유지). -->
          <td colspan="9"></td>
        </tr>`;
    }

    const line = computeRentalLine(row, m.items, fx, m.presets);
    const p    = line?.pricing;
    const chosen = row.rentalPresetId || defaultPresetIdFor(row, m.presets);
    // 미선택('')일 때 첫 옵션을 명시적으로 selected 한다. 안 그러면 브라우저
    //   기본 동작에 맡겨져, 개별 품목 추가 행처럼 아무것도 안 고른 행에서
    //   계산식이 미선택으로 안 보이는 일이 생긴다. 서버 라인은 chosen 이
    //   신품/중고 서버라 그 옵션이 선택된다(defaultPresetIdFor).
    const presetOptions = [
      `<option value="" ${chosen ? '' : 'selected'}>계산식 선택…</option>`,
      ...m.presets.filter((q) => q.is_active || q.id === chosen).map((q) =>
        `<option value="${escapeHtml(q.id)}" ${q.id === chosen ? 'selected' : ''}>${escapeHtml(q.name)}</option>`),
    ].join('');

    // 임대총원가 ⓘ — 원가 항목 내역을 title 로 붙인다. 9줄을 표에 펼치면
    //   행이 너무 길어지고, 저장된 스냅샷과 같은 내용이라 여기서는 요약만 준다.
    const costPopOpen = m.costPopRow === idx;
    const contractCostCell = p
      ? `${fmtKrw(p.contractCostKrw)}
         <button class="deal-quote-info" type="button" title="임대 수익성 조건 보기"
                 data-action="quote-rental-cost-open" data-row-index="${idx}">ⓘ</button>
         ${costPopOpen ? renderRentalCostPopover(p, parseInt0(row.rentalTermMonths) ?? 0) : ''}`
      : '<span class="text-muted">확인 필요</span>';

    // 할증률은 기간이 정한다(꼭짓점 기준 가산/감소) — '단기' 라는 조건이 더는
    // 설명이 되지 않으므로 실제 걸린 비율을 그대로 보여 준다(bdc361d).
    // ★ 이 자리가 병합의 급소였다. 표 재정렬 작업은 개명 전 필드를 보고 있었고,
    //   할증 곡선이 들어오면서 그 필드는 surchargeApplied 로 바뀌어 사라졌다
    //   (shared/rental-pricing.ts 35행). 표 구조는 새 것을 쓰고 할증 표시만
    //   새 필드로 갈아끼운다 — 어느 한쪽만 고르면 컴파일되지 않는다.
    // ★ <div> 가 아니라 <span> 이다. 블록으로 두면 이 셀만 두 줄이 되고,
    //   표가 vertical-align: middle 이라 input 이 옆 칸(임대 기간)보다 위로
    //   올라가 줄이 깨져 보였다(2026-08-20 현민 제보). 아래 wrap 이 입력과
    //   같은 줄에 나란히 놓는다 — 세로로 쌓지 않으면 행 높이가 안 변한다.
    const surchargeNote = p?.surchargeApplied
      ? `<span class="deal-quote-rental-note">기간 할증 ${(Math.round(p.surchargeRate * 1e4) / 1e2)
            .toLocaleString(undefined, { maximumFractionDigits: 2 })}%</span>`
      : '';

    // 임대 단가 — 계산값이 채워져 있고, 고치면 그것이 수동 단가(시트 R열)가 된다.
    //
    // ★ 표시 우선순위는 **수동 > 계산** 이다. 계산값에만 묶어 두면, 매입원가가
    //   아직 없어 계산이 null 인 상태에서 사람이 단가를 넣어도 다시 그리는 순간
    //   칸이 비어 입력이 사라진 것처럼 보인다.
    //
    // ★ '수동' 배지와 되돌리기 버튼은 두지 않는다 (2026-08-20 제거).
    //   기획안의 임대 단가 칸은 값이 채워진 입력칸 하나뿐이다. 칸에 값이 보이고
    //   고칠 수 있으면 그걸로 충분하고, 되돌리려면 비우면 계산값이 다시 들어온다.
    //   수동 단가 **기능** 자체는 남는다 — 시트 R열이자 rental_manual_unit_krw 다.
    const manual   = parseInt0(row.rentalManualUnitKrw);
    const isManual = manual != null && manual > 0;
    // 할증 표시는 입력 **왼쪽**에 둔다. 입력을 오른쪽 끝에 붙여 두어야 옆의
    //   월 임대 금액·금액 합계 열과 오른쪽 정렬 라인이 맞는다. 할증이 없는
    //   행에도 wrap 은 그대로 있으므로 입력 위치가 행마다 흔들리지 않는다.
    const unitCell = `
      <div class="deal-quote-rental-unit-wrap">
        ${surchargeNote}
        <input class="input text-right deal-quote-rental-unit-input" type="text" inputmode="numeric"
               data-action="quote-rental-field" data-rental-field="manualUnitKrw" data-row-index="${idx}"
               value="${escapeHtml(isManual ? fmtThousands(manual)
                                               : (p && p.unitKrw > 0 ? fmtThousands(p.unitKrw) : ''))}"
               placeholder="자동">
      </div>`;

    const marginCls = p?.marginRatePct == null ? 'text-muted'
      : (p.marginRatePct < 0 ? 'text-danger' : 'text-success');

    return `
      <tr class="${rowCls}">
        <td class="deal-quote-item-cell">${partCell}${rowShorthandWarn(row, m.items)}</td>
        <td>${isServer ? '<span class="deal-quote-static">-</span>' : sourceCell(row.isBasePart)}</td>
        <td class="text-right">${isServer
          ? `<span class="deal-quote-static">${escapeHtml(row.quantity)}EA</span>`
          : `<input class="input text-right deal-quote-qty" type="number" min="1"
                    data-action="quote-row-field" data-row-field="quantity" data-row-index="${idx}"
                    value="${escapeHtml(row.quantity)}">`}</td>
        <td class="text-right">${isServer ? '<span class="deal-quote-static">-</span>' : costInput}</td>
        <td class="text-right deal-quote-costsum">${escapeHtml(line ? fmtKrw(line.purchaseKrw) : '확인 필요')}</td>
        <td class="text-right deal-quote-rental-cost">${contractCostCell}</td>
        <td>
          <select class="input deal-quote-rental-preset"
                  data-action="quote-rental-field" data-rental-field="presetId" data-row-index="${idx}">
            ${presetOptions}
          </select>
        </td>
        <td class="text-right">
          <input class="input text-right deal-quote-rental-term" type="number" min="1" max="600"
                 data-action="quote-rental-field" data-rental-field="termMonths" data-row-index="${idx}"
                 value="${escapeHtml(row.rentalTermMonths)}" placeholder="12">
        </td>
        <td class="text-right deal-quote-rental-unit">${unitCell}</td>
        <td class="text-right" data-rental-out="monthly" data-row-index="${idx}">${
          escapeHtml(p && p.monthlyKrw > 0 ? fmtKrw(p.monthlyKrw) : '-')}</td>
        <td class="text-right deal-quote-sellsum">
          <strong data-rental-out="total" data-row-index="${idx}">${
            escapeHtml(p && p.totalKrw > 0 ? fmtKrw(p.totalKrw) : '-')}</strong>
        </td>
        <td class="text-right deal-quote-marginamt ${marginCls}"
            data-rental-out="margin-amt" data-row-index="${idx}">${escapeHtml(
          p && p.totalKrw > 0 ? fmtKrw(p.totalKrw - p.contractCostKrw) : '-')}</td>
        <td class="text-right deal-quote-marginval ${marginCls}"
            data-rental-out="margin" data-row-index="${idx}">${escapeHtml(
          p?.marginRatePct != null ? p.marginRatePct.toFixed(1) + '%' : '확인 필요')}</td>
        <td class="text-center">
          <button class="btn btn-ghost btn-sm text-danger deal-quote-del" type="button"
                  data-action="quote-item-delete" data-row-index="${idx}">삭제</button>
        </td>
      </tr>`;
  }).join('');

  const t = computeRentalTotals(m.items, fx, m.presets);
  return `
    <table class="deal-quote-modal-table deal-quote-modal-table-rental">
      <thead>
        <tr>
          <th>품목</th>
          <th>소스</th>
          <th class="text-right">수량</th>
          <th class="text-right">매입단가</th>
          <th class="text-right" title="이 라인 전체의 매입원가 — 매입단가 × 수량. 소스·매입단가에서 자동으로 유도됩니다(시트 B).">매입금액 합계</th>
          <th class="text-right" title="원가 항목의 합. 시트 O열.">임대총원가</th>
          <th>임대 계산식</th>
          <th class="text-right">임대 기간</th>
          <th class="text-right" title="계산된 단가가 채워져 있습니다. 고치면 그 값이 적용됩니다(시트 R열).">임대 단가</th>
          <th class="text-right">월 임대 금액</th>
          <th class="text-right">금액 합계</th>
          <th class="text-right" title="금액 합계 − 임대총원가.">마진 금액</th>
          <th class="text-right">마진율</th>
          <th class="text-center">작업</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
      <tfoot>
        <tr class="deal-quote-modal-table-total-row">
          <!-- ★ 앞 칸은 5칸이다 — 품목·소스·수량·매입단가·매입금액 합계.
               4칸으로 두면 임대총원가 합계가 **매입금액 합계 열 아래**에
               찍힌다(2026-08-20 발견). 뒤는 그만큼 줄여 4칸 —
               임대 계산식·임대 기간·임대 단가·월 임대 금액. -->
          <td colspan="5" class="text-right text-muted">합계</td>
          <td class="text-right" data-rental-out="foot-cost">${
            escapeHtml(t.complete ? fmtKrw(t.costBasisKrw) : '확인 필요')}</td>
          <td colspan="4"></td>
          <td class="text-right"><strong data-rental-out="foot-total">${
            escapeHtml(t.complete ? fmtKrw(t.totalKrw) : '확인 필요')}</strong></td>
          <td class="text-right" data-rental-out="foot-margin-amt">${
            escapeHtml(t.complete ? fmtKrw(t.marginKrw) : '확인 필요')}</td>
          <td class="text-right" data-rental-out="foot-margin">${
            escapeHtml(t.marginRate != null ? fmtPct(t.marginRate) : '확인 필요')}</td>
          <td></td>
        </tr>
      </tfoot>
    </table>
    <button class="btn btn-ghost btn-sm deal-quote-additem" type="button" data-action="quote-item-add">+ 항목 추가</button>`;
}

/**
 * 임대 하단 상자 — 기획안과 같은 네 개.
 *
 * ★ 판매 모달과 **같은 클래스·같은 항목 구성**이다. 두 모달을 오가도 눈이 같은
 *   자리를 본다. 임대총원가는 표 안에 열로 있으므로 여기서는 빼고, 대신 판매와
 *   같이 VAT 포함 금액을 둔다 — 고객에게 말하는 숫자다.
 */
/**
 * 임대 수익성 조건 팝오버 — 임대총원가 ⓘ 를 누르면 열린다.
 *
 * ★ 기획안(adminuxpreview)은 연 감가율·금융비용·유지보수·물류비·인건비·월 운영비
 *   **여섯 개를 고정으로** 박아 두었다. 그건 프리셋 설계 이전 판이라 그렇고,
 *   우리는 항목을 프리셋으로 관리하므로 **그 견적에 실제로 적용된 항목**을
 *   그대로 편다. 항목 수도 이름도 프리셋마다 다르다.
 *
 * ★ 읽기 전용이다. 기획안은 입력칸으로 그려 두었지만, 여기서 값을 고치면
 *   그 견적에만 적용되는 원가 조건이 생겨 프리셋과 어긋난다 — 저장 경로
 *   (rental_costs 스냅샷)와 재계산까지 걸리는 별건이라 이번 범위 밖이다.
 *
 * 계산 방식 문구는 kind 에서 만든다. 금액만 보여 주면 "이 값이 어디서
 * 나왔는지" 를 설정 화면까지 가서 대조해야 한다.
 */
function rentalCostBasis(kind: string, value: number, months: number): string {
  const pct = (v: number) => `${(Math.round(v * 1e4) / 1e2).toLocaleString('ko-KR', { maximumFractionDigits: 2 })}%`;
  switch (kind) {
    case 'ANNUAL_RATE':   return `매입금액 × 연 ${pct(value)} × ${months}개월 ÷ 12`;
    case 'PURCHASE_RATE': return `매입금액 × ${pct(value)}`;
    case 'FIXED':         return '일회성 고정액';
    case 'FIXED_MONTHLY': return `월 ${fmtKrw(value)} × ${months}개월`;
    default:              return kind;
  }
}

/**
 * 팝오버 좌표 규칙 — **순수 함수**로 뺀다.
 *
 * DOM 을 만지는 쪽(DealDetailView)에 산수를 두면 브라우저 없이는 검증할 수
 * 없다. 잘려 보인 사고가 바로 '눈으로만 확인한 배치' 에서 났으므로, 규칙만
 * 떼어내 화면 밖으로 나가지 않는지·아래가 모자라면 접히는지를 테스트로
 * 못박는다. 뷰는 이 함수가 준 값을 style 에 넣기만 한다.
 *
 * @param a  앵커(ⓘ 버튼)의 뷰포트 좌표 — getBoundingClientRect 의 부분집합
 * @param w  팝오버 너비 / @param h 높이
 * @param vw 뷰포트 너비 / @param vh 높이
 */
export function rentalCostPopoverPos(
  a: { top: number; bottom: number; right: number },
  w: number, h: number, vw: number, vh: number,
): { left: number; top: number } {
  const M = 8;                       // 화면 가장자리 여백
  // 오른쪽 끝을 앵커에 맞춘다 — 임대총원가는 표 가운데라 왼쪽 맞춤이면
  // 오른쪽 열들을 통째로 가린다. 그래도 넘치면 화면 안으로 밀어 넣는다.
  const left  = Math.min(Math.max(M, a.right - w), Math.max(M, vw - w - M));
  const below = a.bottom + 6;
  // 아래가 모자라면 앵커 위로 접는다. 위도 모자라면 최소 여백에 붙인다
  // (잘려 보이더라도 위쪽이 보이는 편이 낫다 — 항목이 위에서부터 읽힌다).
  const top = below + h > vh - M ? Math.max(M, a.top - h - 6) : below;
  return { left, top };
}

/**
 * 레퍼런스 "더보기" 팝오버 위치 — 더보기 버튼 **바로 아래 · 왼쪽 맞춤**으로 떨군다.
 *
 * 임대총원가(ⓘ)와 달리 레퍼런스는 표 가운데-오른쪽 열이다. 임대총원가용
 * 오른쪽-맞춤(rentalCostPopoverPos)을 그대로 쓰면 팝오버가 왼쪽으로 밀려
 * 수량·매입단가·합계 열을 덮는다(현민 제보). 클릭한 자리 바로 아래에 여는 게
 * 자연스럽다. 화면 오른쪽 끝에서 넘칠 때만 안으로 밀어 넣는다.
 */
export function refPopoverPos(
  a: { top: number; bottom: number; left: number },
  w: number, h: number, vw: number, vh: number,
): { left: number; top: number } {
  const M = 8;                       // 화면 가장자리 여백
  const left = Math.min(Math.max(M, a.left), Math.max(M, vw - w - M));
  const below = a.bottom + 4;
  // 아래가 모자라면 버튼 위로 접는다.
  const top = below + h > vh - M ? Math.max(M, a.top - h - 4) : below;
  return { left, top };
}

function renderRentalCostPopover(
  p:      NonNullable<ReturnType<typeof computeRentalLine>>['pricing'],
  months: number,
): string {
  const rows = p.costs.map((c) => `
        <tr>
          <td class="deal-quote-costpop-label">${escapeHtml(c.label)}</td>
          <td class="deal-quote-costpop-basis">${escapeHtml(rentalCostBasis(c.kind, c.value, months))}</td>
          <td class="deal-quote-costpop-amt">${escapeHtml(fmtKrw(c.amountKrw))}</td>
        </tr>`).join('');
  return `
      <div class="deal-tag-pop-overlay" data-action="quote-rental-cost-close"></div>
      <div class="deal-quote-costpop">
        <div class="deal-quote-costpop-head">임대 수익성 조건</div>
        <table class="deal-quote-costpop-table">
          <tbody>
            ${rows || '<tr><td colspan="3" class="text-muted">원가 항목이 없습니다.</td></tr>'}
            <tr class="deal-quote-costpop-sum">
              <td colspan="2">임대총원가</td>
              <td class="deal-quote-costpop-amt">${escapeHtml(fmtKrw(p.contractCostKrw))}</td>
            </tr>
          </tbody>
        </table>
      </div>`;
}

/**
 * 하단 요약 상자 4장만 따로 만든다.
 *
 * ★ 쪼갠 이유는 하나다 — 임대 단가를 타이핑하는 동안 이 네 값도 같이 움직여야
 *   하는데, 그때 모달 전체를 다시 그리면 입력 중인 칸이 새 노드로 교체되어
 *   커서가 튄다. 그래서 `patchRentalUnitOutputs` 가 **이 함수만** 다시 불러
 *   상자 묶음을 통째로 갈아 끼운다. 두 벌로 베껴 두면 다음에 한쪽만 고친다.
 */
function renderRentalSummaryBoxes(m: QuoteModalState): string {
  const t = computeRentalTotals(m.items, parseNum(m.fxRate) ?? 0, m.presets);
  // VAT 규칙은 판매와 같다(summarizeRows). vatIncluded 면 그대로, 아니면 ×1.1.
  const withVat = m.vatIncluded ? t.totalKrw : Math.round(t.totalKrw * 1.1);
  const marginCls = t.marginRate == null ? 'text-muted'
    : t.marginRate < 0 ? 'text-danger' : 'text-success';
  const box = (label: string, value: string, cls = '') => `
        <div class="deal-quote-modal-summary-box">
          <div class="deal-quote-modal-summary-label">${escapeHtml(label)}</div>
          <div class="deal-quote-modal-summary-value ${cls}">${escapeHtml(value)}</div>
        </div>`;
  return `
      <div class="deal-quote-modal-summary">
        ${box('금액 합계 (KRW)',        t.complete ? fmtKrw(t.totalKrw) : '확인 필요')}
        ${box('VAT 포함 금액 합계 (KRW)', t.complete ? fmtKrw(withVat)    : '확인 필요')}
        ${box('마진 금액 (KRW)',        t.complete ? fmtKrw(t.marginKrw) : '확인 필요', marginCls)}
        ${box('전체 마진율',            t.marginRate != null ? fmtPct(t.marginRate) : '확인 필요', marginCls)}
      </div>`;
}

/**
 * 임대 단가를 **타이핑하는 동안** 파생 값만 제자리에서 갈아 끼운다.
 *
 * ★ 왜 필요한가 ─────────────────────────────────────────────────────
 *   서안드레(2026-08-20 슬랙): "처음에 목표마진율에 따라 자동으로 계산되어
 *   채워집니다. 수동으로 적으면 **그 때부터** 해당 금액으로 재계산됩니다."
 *   기획안도 같다 — 임대 단가 칸은 `input` 이벤트마다 재계산하고(32427행),
 *   결과 셀만 innerHTML 로 갈아 끼운다(updateQuoteModalCalculatedValues).
 *
 *   우리는 지금까지 `change`(포커스가 빠질 때)에만 다시 그렸다. 단가를 치는
 *   동안 월 임대 금액·금액 합계·마진율이 옛 값 그대로 서 있어서, 얼마를
 *   적어야 마진이 얼마가 되는지 보면서 맞출 수가 없었다.
 *
 * ★ 왜 refreshQuoteModal 을 부르지 않는가 ───────────────────────────
 *   그건 모달 전체를 innerHTML 로 교체한다. 지금 타이핑 중인 input 이 새
 *   노드로 갈리면서 커서가 끝으로 튄다(그래서 원래 change 로 미뤄 뒀던 것이다).
 *   그래서 **입력칸은 손대지 않고** 결과 셀만 textContent 로 덮는다.
 *   검색 결과만 갈아 끼우는 `_updateCustomerModalResults` 와 같은 수법이다.
 *
 * ★ 값은 화면이 아니라 **모델**에서 다시 계산한다. 렌더러와 똑같이
 *   `computeRentalLine` / `computeRentalTotals` 를 부르므로 두 경로가
 *   갈라질 수 없다. 여기서 식을 다시 쓰면 그날로 두 벌이 된다.
 *
 * ★ 임대 기간 칸은 이 함수의 대상이 아니다. 기간이 바뀌면 임대총원가·할증·
 *   자동 단가·ⓘ 팝오버까지 행 전체가 달라져서 '결과 셀 몇 개'로 끝나지
 *   않는다. 기간은 지금처럼 change 에서 전체를 다시 그린다.
 */
export function patchRentalUnitOutputs(root: ParentNode, m: QuoteModalState): void {
  const fx = parseNum(m.fxRate) ?? 0;

  const put = (sel: string, text: string): HTMLElement | null => {
    const el = root.querySelector<HTMLElement>(sel);
    if (el) el.textContent = text;
    return el;
  };
  const paintMargin = (el: HTMLElement | null, pct: number | null): void => {
    if (!el) return;
    el.classList.remove('text-muted', 'text-danger', 'text-success');
    el.classList.add(pct == null ? 'text-muted' : pct < 0 ? 'text-danger' : 'text-success');
  };

  m.items.forEach((row, idx) => {
    const at = (k: string) => `[data-rental-out="${k}"][data-row-index="${idx}"]`;
    // 접힌 서버 구성품·자식행은 애초에 DOM 에 없다 — 없으면 조용히 건너뛴다.
    if (!root.querySelector(at('monthly'))) return;
    const p = computeRentalLine(row, m.items, fx, m.presets)?.pricing;
    put(at('monthly'), p && p.monthlyKrw > 0 ? fmtKrw(p.monthlyKrw) : '-');
    put(at('total'),   p && p.totalKrw   > 0 ? fmtKrw(p.totalKrw)   : '-');
    paintMargin(
      put(at('margin'), p?.marginRatePct != null ? p.marginRatePct.toFixed(1) + '%' : '확인 필요'),
      p?.marginRatePct ?? null,
    );
    paintMargin(
      put(at('margin-amt'), p && p.totalKrw > 0 ? fmtKrw(p.totalKrw - p.contractCostKrw) : '-'),
      p?.marginRatePct ?? null,
    );
  });

  const t = computeRentalTotals(m.items, fx, m.presets);
  put('[data-rental-out="foot-cost"]',   t.complete ? fmtKrw(t.costBasisKrw) : '확인 필요');
  put('[data-rental-out="foot-total"]',  t.complete ? fmtKrw(t.totalKrw)     : '확인 필요');
  put('[data-rental-out="foot-margin-amt"]', t.complete ? fmtKrw(t.marginKrw) : '확인 필요');
  put('[data-rental-out="foot-margin"]', t.marginRate != null ? fmtPct(t.marginRate) : '확인 필요');

  // 하단 상자 4장은 입력칸이 없으므로 통째로 교체해도 커서 문제가 없다.
  const summary = root.querySelector('.deal-quote-modal-summary');
  if (summary) summary.outerHTML = renderRentalSummaryBoxes(m);
}

function renderRentalModalTotals(m: QuoteModalState): string {
  return `
      ${renderRentalSummaryBoxes(m)}
      <p class="text-muted text-sm deal-quote-rental-foot">
        금액 합계는 임대 단가 × 수량 × 기간입니다. 중도해지 위약금은 견적서에
        인쇄하지 않습니다 — 견적 시점에는 잔여개월이 없습니다.
      </p>
    `;
}


// ─── 임대 견적 **보기** 모달 ────────────────────────────────────────
//
// ★ 여기서는 **다시 계산하지 않는다.** 전부 저장된 값을 읽어 표시만 한다.
//   재계산하면 나중에 프리셋(마진율·할증 곡선·원가 항목)을 고쳤을 때 이미
//   발송한 견적의 숫자가 소급해서 흔들린다. quote_item 에 임대 열 9개와 원가
//   스냅샷을 통째로 저장해 둔 이유가 그것이고, 판매 보기 모달도 같은 원칙이다.
//
//   표시값의 출처:
//     임대총원가   rental_contract_cost_krw   (라인 전체 = 수량·기간 반영된 C)
//     임대 기간    rental_term_months
//     임대 단가    unit_price_krw             (수동 단가가 있으면 저장 시 그 값)
//     월 임대 금액 임대 단가 × 수량
//     금액 합계    월 임대 금액 × 기간
//     마진율       (금액 합계 − 임대총원가) ÷ 금액 합계
//   마지막 둘은 shared 의 rental-pricing 이 쓰는 식과 같은 정의를 저장값에
//   그대로 적용한 것이다 — 프리셋을 다시 읽지 않으므로 소급 변동이 없다.
//
// ★ 원가 항목 9줄(rental_costs)은 여기에 펼치지 않는다. 기획안에도 없고,
//   임대총원가 한 값이면 이 화면의 목적(발송 전 확인)에는 충분하다.

/** 라인의 임대 파생값. 저장값만 쓴다. */
function rentalViewLine(it: api.QuoteItem): {
  months: number; unitKrw: number; monthlyKrw: number;
  totalKrw: number; contractCostKrw: number | null; marginRatePct: number | null;
} | null {
  const months = it.rental_term_months ?? 0;
  if (months <= 0) return null;                 // 임대 라인이 아니다(서버 구성품 등)
  const qty     = it.quantity ?? 0;
  const unitKrw = it.unit_price_krw ?? 0;
  const monthlyKrw = unitKrw * qty;
  const totalKrw   = monthlyKrw * months;
  const contractCostKrw = it.rental_contract_cost_krw ?? null;
  const marginRatePct = (totalKrw > 0 && contractCostKrw != null)
    ? ((totalKrw - contractCostKrw) / totalKrw) * 100
    : null;
  return { months, unitKrw, monthlyKrw, totalKrw, contractCostKrw, marginRatePct };
}

/**
 * 임대 항목 표(보기) — 기획안 열 11개.
 *
 * 품목 · 소스 · 수량 · 매입단가 · 매입금액합계 · 임대총원가 ·
 * 임대 기간 · 임대 단가 · 월 임대 금액 · 금액 합계 · 마진율
 *
 * ★ 매입단가는 판매 보기와 같이 편집 가능하다(같은 quote-view-cost-change).
 *   실제 매입가를 나중에 확정하는 실무 흐름이 임대에도 똑같이 있어서다.
 *   다만 고쳐도 **마진율은 움직이지 않는다** — 임대 마진은 저장된 임대총원가
 *   기준이고 이 화면은 재계산하지 않기 때문이다. 매입금액 합계만 반응한다.
 */
function renderQuoteViewRentalTable(
  items:        api.QuoteItem[],
  quoteSources: api.DealRfqSource[],
  fx:           number,
): string {
  if (items.length === 0) {
    return `<p class="text-muted text-center" style="padding: 16px;">견적 항목이 없습니다.</p>`;
  }
  const frameRows = items.map((it) => ({
    lineType:         it.line_type ?? 'part',
    parentDealItemId: it.parent_deal_item_id ?? null,
    deal_item_id:     it.deal_item_id ?? null,
  }));
  // 판매 보기와 같은 판정 — 매입단가가 하나라도 입력됐으면 빈 칸을 0 으로 본다.
  const zeroCost = items.some((it) => (it.line_type ?? 'part') !== 'server'
                                   && !(it.is_base_part ?? false)
                                   && it.cost_basis_amount != null);
  // 서버 헤더행의 매입금액 합계 = 자식 합(판매 표와 같은 규칙). fx=costFx 를 받는다.
  const srvCost = serverChildCostSums(items, fx);

  const rows = items.map((it, idx) => {
    const qty = it.quantity ?? 0;
    const costKrw = it.cost_basis_amount != null
      ? (it.cost_basis_currency === 'KRW'
          ? Math.round(it.cost_basis_amount)
          : (fx > 0 ? Math.round(it.cost_basis_amount * fx) : null))
      : (zeroCost || (it.is_base_part ?? false) ? 0 : null);
    const lineCost = costKrw != null ? costKrw * qty : null;

    const matches = quoteSources.filter((s) => s.option_part === it.option_part);
    const srcOpts = ['<option value="">선택 없음</option>',
      ...matches.map((s) => {
        const sel = it.source_ref_id === String(s.rfq_id) ? 'selected' : '';
        return `<option value="${s.rfq_id}" ${sel}>${escapeHtml(quoteSourceLabel(s))}</option>`;
      })].join('');

    const isSrvHdr = it.line_type === 'server';
    const isSrvKid = it.parent_deal_item_id != null;
    const rowCls   = serverFrameClass(frameRows, idx, () => true);
    const partLine = [it.option_part, it.description, it.requested_condition]
      .filter(Boolean).map((x) => escapeHtml(String(x))).join(' ');
    const partCell = isSrvHdr
      ? `<div class="deal-quote-server-model">${escapeHtml(withServerSuffix(it.description) || '서버')}</div>
         <div class="deal-quote-server-cond">${escapeHtml(String(it.server_quantity ?? 1))}대</div>`
      : isSrvKid
        ? `<div class="deal-quote-child-head">
             <span class="deal-quote-child-cat">${escapeHtml(it.category ?? '-')}</span>
             <span class="deal-quote-child-part">${escapeHtml(it.option_part ?? '파트넘버 미지정')}</span>
             ${basePartChip(it.is_base_part ?? false)}${regChip(it.is_unregistered, it.is_pending)}
           </div>
           <div class="deal-quote-child-desc">${escapeHtml(it.description ?? '')}</div>`
        : `<div class="deal-quote-item-part">${partLine || '-'}${basePartChip(it.is_base_part ?? false)}${regChip(it.is_unregistered, it.is_pending)}</div>
           ${it.category ? `<div class="deal-quote-item-cat">${escapeHtml(it.category)}</div>` : ''}`;

    const p = rentalViewLine(it);
    const marginCls = p?.marginRatePct == null ? 'text-muted'
      : (p.marginRatePct < 0 ? 'text-danger' : 'text-success');
    // 임대 열 6개 — 임대 라인(기간이 저장된 행)만 값이 있고 구성품은 '-' 다.
    const rentalCells = p
      ? `<td class="text-right deal-quote-rental-cost">${p.contractCostKrw != null ? fmtKrw(p.contractCostKrw) : '확인 필요'}</td>
         <td class="text-right">${escapeHtml(String(p.months))}개월</td>
         <td class="text-right">${fmtKrw(p.unitKrw)}</td>
         <td class="text-right">${fmtKrw(p.monthlyKrw)}</td>
         <td class="text-right deal-quote-sellsum"><strong>${fmtKrw(p.totalKrw)}</strong></td>
         <td class="text-right deal-quote-marginamt ${marginCls}">${p.contractCostKrw != null ? fmtKrw(p.totalKrw - p.contractCostKrw) : '확인 필요'}</td>
         <td class="text-right deal-quote-marginval ${marginCls}">${p.marginRatePct != null ? fmtPct(p.marginRatePct) : '확인 필요'}</td>`
      : `<td class="text-right text-muted">-</td>
         <td class="text-right text-muted">-</td>
         <td class="text-right text-muted">-</td>
         <td class="text-right text-muted">-</td>
         <td class="text-right text-muted">-</td>
         <td class="text-right text-muted">-</td>
         <td class="text-right text-muted">-</td>`;

    return `
        <tr class="${rowCls}">
          <td class="deal-quote-item-cell">${partCell}${rowShorthandWarn(it, items)}</td>
          <td>${isSrvHdr
            ? '<span class="deal-quote-static">-</span>'
            : `<select class="input deal-quote-source-select"
                 ${it.is_base_part
                   ? 'disabled title="기본 구성 품목은 섀시 값에 포함되어 소스를 고르지 않습니다."'
                   : `data-action="quote-view-source-change" data-item-index="${idx}"
                      ${matches.length === 0 ? 'disabled title="이 품목의 RFQ 가격 없음"' : ''}`}>
                 ${srcOpts}
               </select>`}</td>
          <td class="text-right">${qty || '-'}</td>
          <td class="text-right">${isSrvHdr
            ? '<span class="deal-quote-static">-</span>'
            : `<input class="input text-right deal-quote-price" type="text" inputmode="numeric"
                      data-action="quote-view-cost-change" data-item-index="${idx}"
                      value="${escapeHtml(costKrw != null ? fmtThousands(costKrw) : '')}" placeholder="0"
                      ${it.is_base_part ? 'disabled title="기본 구성 품목은 섀시 값에 포함되어 매입원가가 0입니다."' : ''}>`}</td>
          <td class="text-right deal-quote-costsum">${
            isSrvHdr
              ? serverCostSumCell(true, it.deal_item_id, srvCost, false, null)
              : (lineCost != null ? fmtKrw(lineCost) : '확인 필요')}</td>
          ${rentalCells}
        </tr>`;
  }).join('');

  return `
      <table class="deal-quote-modal-table deal-quote-modal-table-v2 deal-quote-modal-table-rental">
        <thead>
          <tr>
            <th>품목</th>
            <th>소스</th>
            <th class="text-right">수량</th>
            <th class="text-right">매입단가</th>
            <th class="text-right">매입금액 합계</th>
            <th class="text-right">임대총원가</th>
            <th class="text-right">임대 기간</th>
            <th class="text-right">임대 단가</th>
            <th class="text-right">월 임대 금액</th>
            <th class="text-right">금액 합계</th>
            <th class="text-right" title="금액 합계 − 임대총원가.">마진 금액</th>
            <th class="text-right">마진율</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `;
}

/**
 * 임대 합계 카드 4장 — 금액 합계 · VAT 포함 · 마진 금액 · 전체 마진율.
 *
 * 판매(5장)와 달리 **매입 합계가 없다**. 임대에서 마진의 상대는 매입원가가
 * 아니라 임대총원가(물류·인건·감가까지 포함한 C)이기 때문이다.
 * 값이 없을 때의 표기는 판매 보기와 같은 관례를 쓴다 — '확인 필요' / '-'.
 */
function renderQuoteViewRentalTotals(items: api.QuoteItem[]): string {
  let totalKrw = 0;
  let costKrw  = 0;
  let costKnown = true;
  for (const it of items) {
    const p = rentalViewLine(it);
    if (!p) continue;
    totalKrw += p.totalKrw;
    if (p.contractCostKrw == null) costKnown = false;
    else costKrw += p.contractCostKrw;
  }
  const withVat    = Math.round(totalKrw * 1.1);   // 보기 모달은 항상 VAT 별도
  const marginKrw  = costKnown ? totalKrw - costKrw : null;
  const marginRate = (marginKrw != null && totalKrw > 0) ? (marginKrw / totalKrw) * 100 : null;
  const marginCls  = marginRate == null ? 'text-muted'
    : marginRate < 0 ? 'text-danger' : 'text-success';
  const box = (label: string, value: string, cls = '') => `
        <div class="deal-quote-modal-summary-box">
          <div class="deal-quote-modal-summary-label">${escapeHtml(label)}</div>
          <div class="deal-quote-modal-summary-value ${cls}">${escapeHtml(value)}</div>
        </div>`;
  return `
      <div class="deal-quote-modal-summary">
        ${box('금액 합계 (KRW)',          totalKrw > 0 ? fmtKrw(totalKrw) : '-')}
        ${box('VAT 포함 금액 합계 (KRW)', totalKrw > 0 ? fmtKrw(withVat)  : '-')}
        ${box('마진 금액 (KRW)',          marginKrw != null ? fmtKrw(marginKrw) : '확인 필요', marginCls)}
        ${box('전체 마진율',              marginRate != null ? fmtPct(marginRate) : '확인 필요', marginCls)}
      </div>
    `;
}

// ─── 견적서 다운로드 종류 선택 (§13-1) ──────────────────────────────
/**
 * 서버 품목이 있는 견적만 뜬다. 카드 두 장 + 각 카드 우측 다운로드 버튼.
 *
 * backdrop 은 이 파일에서 **가장 마지막**에 선언한다 — Sola 위임은 선언 순서상
 * 첫 매치를 쓰므로, backdrop 을 먼저 두면 안쪽 버튼 클릭까지 backdrop 이 먹는다.
 */
export function renderQuoteDownloadModal(m: QuoteDownloadModalState): string {
  if (!m.open) return '';
  const card = (
    kind: 'server-full' | 'server-brief', title: string, desc: string,
  ): string => `
    <div class="deal-qdl-card">
      <div class="deal-qdl-card-text">
        <div class="deal-qdl-card-title">${escapeHtml(title)}</div>
        <div class="deal-qdl-card-desc">${escapeHtml(desc)}</div>
      </div>
      <button class="btn btn-primary deal-qdl-btn" type="button"
              data-action="quote-download-pick" data-preset="${kind}"
              ${m.busy ? 'disabled' : ''}>
        ${m.busy === kind ? '생성 중…' : '다운로드'}
      </button>
    </div>`;
  return `
    <div class="deal-qdl-modal">
      <h3 class="deal-qdl-title">서버 견적서 다운로드</h3>
      <p class="deal-qdl-sub">${escapeHtml(m.quoteName)} · 서버 구성 템플릿을 선택합니다.</p>
      ${card('server-full',  '정식 견적서', '서버 상세 사양과 파트넘버를 모두 표시합니다.')}
      ${card('server-brief', '약식 견적서', '서버 일괄금액을 유지하고 상세 사양은 숨깁니다.')}
      ${m.error ? `<p class="deal-qdl-error">${escapeHtml(m.error)}</p>` : ''}
      <div class="deal-qdl-foot">
        <button class="btn btn-ghost" type="button" data-action="quote-download-close"
                ${m.busy ? 'disabled' : ''}>취소</button>
      </div>
    </div>
    <div class="deal-qdl-backdrop" data-action="quote-download-close"></div>`;
}
